import {
  calculateAgeDetailsFromBirthdate,
  parseMMDDYYYY,
} from "@/modules/add-new-data/lib/age";
import { cleanRBIMCode, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import {
  resolveResidentBirthdate,
  type RbimHouseholdProfile,
  type RbimResidentProfile,
} from "@/lib/rbim-storage";

export type ReportScopeOptions = {
  barangay?: string;
  municipality?: string;
  municipalityWide: boolean;
};

export type ReportDefinition = {
  id: number;
  title: string;
};

export type ReportColumn = {
  key: string;
  label: string;
  children?: ReportColumn[];
};

export type ReportRow = Record<string, string | number | boolean | undefined> & {
  isTotal?: boolean;
};

export type ReportTableData = {
  reportId: number;
  title: string;
  scopeLabel: string;
  generatedAt: string;
  residentCount: number;
  householdCount: number;
  isImplemented: boolean;
  columns: ReportColumn[];
  rows: ReportRow[];
  notes?: string[];
};

type Sex = "Male" | "Female" | "Invalid";
type MigrationStatus = "Non-Migrant" | "Migrant" | "Transient" | "Invalid";
type Category = { key: string; label: string };
type ReportContext = {
  scopeLabel: string;
  residentCount: number;
  householdCount: number;
};

export const REPORT_DEFINITIONS: ReportDefinition[] = [
  { id: 1, title: "Total Population by Sex, Age, and Migration Status" },
  { id: 2, title: "Distribution of Households by Household Size, Sex of Household Head and Migration Status" },
  { id: 3, title: "Total Population by Age, Marital Status and Migration Status" },
  { id: 4, title: "Total Population by Marital Status, Sex, and Migration Status" },
  { id: 5, title: "Total Population by Religion, Sex, and Migration Status" },
  { id: 6, title: "Total Population by Ethnicity, Sex, and Migration Status" },
  { id: 7, title: "Population Ages 5 and Over by Highest Educational Attainment, Sex, and Migration Status" },
  { id: 8, title: "Population Age 3-24 by Current Enrollment Status, School Type, Sex and Migration Status" },
  { id: 9, title: "Population Age 3-24 Currently Enrolled by School Level, Sex and Migration Status" },
  { id: 10, title: "Population Age 15 and Over by Monthly Income, Sex and Migration Status" },
  { id: 11, title: "Population Age 15 and Over by Major Source of Income, Sex and Migration Status" },
  { id: 12, title: "Population Age 15 and Over by Status of Work/Business, Sex and Migration Status" },
  { id: 13, title: "Children Age 0-11 Months by Place of Delivery and Sex" },
  { id: 14, title: "Distribution of Children Age 0-11 Months by Type of Birth Attendant and Sex" },
  { id: 15, title: "Distribution of Children Age 0-11 Months by Type of Last Immunization Received and Sex" },
  { id: 16, title: "Distribution of Women 10-54 Years Old by Gravida and Migration Status" },
  { id: 17, title: "Distribution of Women Age 10-54 by Number of Living Children and Migration Status" },
  { id: 18, title: "Distribution of Women Age 10-54 by Current Use of Contraception and Migration Status" },
  { id: 19, title: "Distribution of Women Age 10-54 by Number of Living Children, Current Use of Contraception and Migration Status" },
  { id: 20, title: "Distribution of Women Age 10-54 Currently Using Modern FP Methods by Source and Migration Status" },
  { id: 21, title: "Distribution of Women Age 10-54 Not Currently Using FP Method by Intention to Use FP Method and Migration Status" },
  { id: 22, title: "Distribution of Non-FP Users with Intention to Use by Preferred Method of Contraception and Migration" },
  { id: 23, title: "Total Population with Health Insurance Coverage by Age, Sex and Migration Status" },
  { id: 24, title: "Total Population that Visited Health Facility by Reason for Seeking Health Care, Sex and Migration Status" },
  { id: 25, title: "Total Population by Background Characteristics, Health Facility Visited and Migration Status" },
  { id: 26, title: "Distribution of Population of Persons with Disability by Type of Disability, Age, Sex and Migration Status" },
  { id: 27, title: "Distribution of Reported Deaths Among Women by Age Group" },
  { id: 28, title: "Distribution of Reported Deaths Among Women by Cause of Death" },
  { id: 29, title: "Distribution of Reported Deaths Among Children Age 5 and Below by Sex" },
  { id: 30, title: "Distribution of Reported Deaths Among Children Age 5 and Below by Cause of Death and Sex" },
  { id: 31, title: "Distribution of Reported Common Diseases that Cause Deaths" },
  { id: 32, title: "Distribution of Parent Population by Parentage Status, Sex and Migration Status" },
  { id: 33, title: "Distribution of Population Age 60 and Over by Senior Citizen Registration Status, Sex and Migration" },
  { id: 34, title: "Distribution of Population Age 15 and Over by Voter Registration Status, Sex and Migration Status" },
  { id: 35, title: "Distribution of Population Age 15 and Over by Type of Skills, Age, Sex and Migration Status" },
  { id: 36, title: "Distribution of Primary Needs of the Barangay" },
  { id: 37, title: "Distribution of Migrant Population by Year of Transfer to Current Barangay, Age, Sex and Migration" },
  { id: 38, title: "Distribution of Migrant Population by Reason for Leaving Previous Barangay, Age, Sex and Migration" },
  { id: 39, title: "Distribution of Migrant Population by Reason for Transferring to Current Barangay, Age, Sex and Migration" },
  { id: 40, title: "Distribution of Migrant Population by Intended Length of Stay in Current Barangay, Age, Sex and Migration" },
  { id: 41, title: "Distribution of Household Population by Intended Residence in the Future" },
  { id: 42, title: "Distribution of Household Population by Housing Characteristics" },
];

const ageGroups0To80 = [
  "0-4",
  "5-9",
  "10-14",
  "15-19",
  "20-24",
  "25-29",
  "30-34",
  "35-39",
  "40-44",
  "45-49",
  "50-54",
  "55-59",
  "60-64",
  "65-69",
  "70-74",
  "75-79",
  "80+",
] as const;

const ageGroups3To24 = ["3-4", "5-9", "10-14", "15-19", "20-24"] as const;
const ageGroups5AndOverEducation = ["6-9", "10-14", "15-19", "20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "65+"] as const;
const ageGroups15AndOver = ["15-19", "20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "65-69", "70-74", "75-79", "80+"] as const;
const ageGroups10To54 = ["10-14", "15-19", "20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-54"] as const;

const migrationGroups = [
  { key: "nonMigrant", label: "Non-Migrants", value: "Non-Migrant" },
  { key: "migrant", label: "Migrants", value: "Migrant" },
  { key: "transient", label: "Transients", value: "Transient" },
] as const;

const sexCategories = [
  { key: "male", label: "Male", value: "Male" },
  { key: "female", label: "Female", value: "Female" },
] as const;

const civilStatusCategories: Category[] = [
  { key: "single", label: "Single" },
  { key: "married", label: "Married" },
  { key: "livingIn", label: "Living-in" },
  { key: "widowed", label: "Widowed" },
  { key: "separated", label: "Separated" },
  { key: "divorced", label: "Divorced" },
  { key: "unknown", label: "Unknown" },
];

const educationCategories: Category[] = [
  { key: "noEducation", label: "No Education" },
  { key: "preSchool", label: "Pre-school" },
  { key: "elementaryLevel", label: "Elementary Level" },
  { key: "elementaryGraduate", label: "Elementary Graduate" },
  { key: "highSchoolLevel", label: "High School Level" },
  { key: "highSchoolGraduate", label: "High School Graduate" },
  { key: "juniorHsLevel", label: "Junior HS Level" },
  { key: "juniorHsGraduate", label: "Junior HS Graduate" },
  { key: "seniorHsLevel", label: "Senior HS Level" },
  { key: "seniorHsGraduate", label: "Senior HS Graduate" },
  { key: "vocationalTechnical", label: "Vocational/Technical" },
  { key: "collegeLevel", label: "College Level" },
  { key: "collegeGraduate", label: "College Graduate" },
  { key: "postGraduate", label: "Post-Graduate" },
];

const enrollmentCategories: Category[] = [
  { key: "public", label: "Enrolled in Public" },
  { key: "private", label: "Enrolled in Private" },
  { key: "notEnrolled", label: "Not Enrolled" },
];

const schoolLevelCategories: Category[] = [
  { key: "preSchool", label: "Pre-school" },
  { key: "elementary", label: "Elementary" },
  { key: "juniorHs", label: "Junior HS" },
  { key: "seniorHs", label: "Senior HS" },
  { key: "vocationalTechnical", label: "Vocational/Technical" },
  { key: "collegeUniversity", label: "College/University" },
];

const incomeCategories: Category[] = [
  { key: "under5000", label: "Under 5,000" },
  { key: "5000To9999", label: "5,000 - 9,999" },
  { key: "10000To14999", label: "10,000 - 14,999" },
  { key: "15000To19999", label: "15,000 - 19,999" },
  { key: "20000To24999", label: "20,000 - 24,999" },
  { key: "25000AndAbove", label: "25,000 and Above" },
];

const incomeSourceCategories: Category[] = [
  { key: "employment", label: "Employment" },
  { key: "business", label: "Business" },
  { key: "remittance", label: "Remittance" },
  { key: "investments", label: "Investments" },
  { key: "others", label: "Others" },
];

const workStatusCategories: Category[] = [
  { key: "permanent", label: "Permanent Work" },
  { key: "casual", label: "Casual Work" },
  { key: "contractual", label: "Contractual Work" },
  { key: "individualBusiness", label: "Individually Owned Business" },
  { key: "partnership", label: "Shared/Partnership" },
  { key: "corporate", label: "Corporate Business" },
];

const deliveryPlaceCategories: Category[] = [
  { key: "publicHospital", label: "Public hospital" },
  { key: "privateHospital", label: "Private hospital" },
  { key: "lyingInClinic", label: "Lying-in clinic" },
  { key: "home", label: "Home" },
  { key: "others", label: "Others" },
];

const birthAttendantCategories: Category[] = [
  { key: "doctor", label: "Doctor" },
  { key: "nurse", label: "Nurse" },
  { key: "midwife", label: "Midwife" },
  { key: "hilot", label: "Hilot" },
  { key: "others", label: "Others" },
];

const contraceptionCategories: Category[] = [
  { key: "femaleSterilization", label: "Female Sterilization/Ligation" },
  { key: "maleSterilization", label: "Male Sterilization/Vasectomy" },
  { key: "iud", label: "IUD" },
  { key: "injectables", label: "Injectables" },
  { key: "implants", label: "Implants" },
  { key: "pills", label: "Pills" },
  { key: "condom", label: "Condom" },
  { key: "modernNaturalFp", label: "Modern Natural FP" },
  { key: "lam", label: "LAM" },
  { key: "none", label: "None" },
];

const fpSourceCategories: Category[] = [
  { key: "governmentHospital", label: "Government Hospital" },
  { key: "rhuHealthCenter", label: "RHU / Health Center" },
  { key: "barangayHealthStation", label: "Brgy. Health Station" },
  { key: "privateHospital", label: "Private Hospital" },
  { key: "pharmacy", label: "Pharmacy" },
];

const fpIntentionCategories: Category[] = [
  { key: "withIntention", label: "With Intention" },
  { key: "withoutIntention", label: "Without Intention" },
];

const preferredFpMethodCategories: Category[] = [
  ...contraceptionCategories.filter((category) => category.key !== "none"),
  { key: "traditional", label: "Traditional" },
  { key: "others", label: "Others" },
];

const healthInsuranceCategories: Category[] = [
  { key: "philHealthPaying", label: "PhilHealth Paying Member" },
  { key: "philHealthDependentPaying", label: "PhilHealth Dependent of Paying Member" },
  { key: "philHealthIndigent", label: "PhilHealth Indigent Member" },
  { key: "philHealthDependentIndigent", label: "PhilHealth Dependent of Indigent Member" },
  { key: "gsis", label: "GSIS" },
  { key: "sss", label: "SSS" },
  { key: "privateHmo", label: "PRIVATE/HMO" },
  { key: "others", label: "Others" },
];

const healthFacilityReasonCategories: Category[] = [
  { key: "sickInjured", label: "Sick/Injured" },
  { key: "prenatalPostnatal", label: "Prenatal/Postnatal" },
  { key: "gaveBirth", label: "Gave birth" },
  { key: "dental", label: "Dental" },
  { key: "medicalCheckup", label: "Medical check-up" },
  { key: "medicalRequirement", label: "Medical requirement" },
  { key: "nhtsCct4ps", label: "NHTS/CCT/4Ps requirement" },
  { key: "others", label: "Others" },
];

const healthFacilityTypeCategories: Category[] = [
  { key: "governmentHospital", label: "Government Hospital" },
  { key: "rhuHealthCenter", label: "RHU / Health Center" },
  { key: "barangayHealthStation", label: "Brgy. Health Station" },
  { key: "privateHospital", label: "Private Hospital" },
  { key: "privateClinic", label: "Private Clinic" },
  { key: "pharmacy", label: "Pharmacy" },
  { key: "hilotHerbalist", label: "Hilot / Herbalist" },
  { key: "others", label: "Others" },
];

const disabilityTypeCategories: Category[] = [
  { key: "sensory", label: "Sensory Disability" },
  { key: "physical", label: "Physical Disability" },
  { key: "mental", label: "Mental Disability" },
];

const parentageCategories: Category[] = [
  { key: "registeredSoloParent", label: "Registered Solo Parent" },
  { key: "nonSoloParent", label: "Non Solo Parent" },
  { key: "unregisteredSoloParent", label: "Unregistered Solo Parent" },
];

const seniorCitizenCategories: Category[] = [
  { key: "yes", label: "Yes, registered Senior Citizen" },
  { key: "no", label: "No, not registered Senior Citizen" },
];

const voterCategories: Category[] = [
  { key: "registeredBarangay", label: "Registered Voter in the barangay" },
  { key: "registeredOtherBarangay", label: "Registered in another barangay" },
  { key: "unregistered", label: "Unregistered" },
];

const skillCategories: Category[] = [
  { key: "refrigerationAirconditioning", label: "Refrigeration and Airconditioning" },
  { key: "automotiveHeavyEquipment", label: "Automotive / Heavy Equipment Servicing" },
  { key: "metalWorker", label: "Metal Worker" },
  { key: "buildingWiring", label: "Building Wiring Installation" },
  { key: "heavyEquipment", label: "Heavy Equipment Operation" },
  { key: "plumbing", label: "Plumbing" },
  { key: "welding", label: "Welding" },
  { key: "carpentry", label: "Carpentry" },
  { key: "baking", label: "Baking" },
  { key: "dressmaking", label: "Dressmaking" },
  { key: "linguist", label: "Linguist" },
  { key: "computerGraphics", label: "Computer Graphics" },
  { key: "painting", label: "Painting" },
  { key: "beautyCare", label: "Beauty Care" },
  { key: "commercialCooking", label: "Commercial Cooking" },
  { key: "housekeeping", label: "Housekeeping" },
  { key: "massageTherapy", label: "Massage Therapy" },
  { key: "others", label: "Others" },
];

const transferYearCategories: Category[] = [
  { key: "transientSixMonthsAgo", label: "Transient 6 months ago" },
  { key: "oneYearAgo", label: "1 Year ago" },
  { key: "twoYearsAgo", label: "2 Years ago" },
  { key: "threeYearsAgo", label: "3 Years ago" },
  { key: "fourYearsAgo", label: "4 Years ago" },
];

const reasonLeavingCategories: Category[] = [
  { key: "lackEmployment", label: "Lack of Employment" },
  { key: "betterIncome", label: "Perception of better income in other place" },
  { key: "schooling", label: "Schooling" },
  { key: "relativesFriends", label: "Presence of relatives and friends in other place" },
  { key: "jobRelocation", label: "Employment / Job Relocation" },
  { key: "disasterRelocation", label: "Disaster-related Relocation" },
  { key: "retirement", label: "Retirement" },
  { key: "liveWithParents", label: "To live with Parents" },
  { key: "liveWithChildren", label: "To live with Children" },
  { key: "marriage", label: "Marriage" },
  { key: "separation", label: "Annulment / Divorce / Separation" },
  { key: "commuting", label: "Commuting-related Reasons" },
  { key: "health", label: "Health-related Reasons" },
  { key: "peaceSecurity", label: "Peace and Security" },
  { key: "others", label: "Others" },
];

const reasonTransferringCategories: Category[] = [
  { key: "availabilityJobs", label: "Availability of jobs" },
  { key: "higherWage", label: "Higher wage" },
  { key: "schoolsUniversities", label: "Presence of schools or universities" },
  { key: "relativesFriends", label: "Presence of relatives and friends in other place" },
  { key: "housing", label: "Housing" },
  { key: "others", label: "Others" },
];

const intendedLengthOfStayCategories: Category[] = [
  { key: "oneYear", label: "1 Year" },
  { key: "twoYears", label: "2 Years" },
  { key: "threeYears", label: "3 Years" },
  { key: "fourYears", label: "4 Years" },
  { key: "fiveYears", label: "5 Years" },
  { key: "sixYearsAndUp", label: "6 Years and up" },
  { key: "willNotTransfer", label: "Will not transfer" },
];

const intendedResidenceCategories: Category[] = [
  { key: "currentBarangay", label: "Current barangay" },
  { key: "otherPlace", label: "Other Place" },
];

const housingCharacteristicCategories: Category[] = [
  { key: "rentFreeWithoutConsent", label: "Rent-free without consent of owner" },
  { key: "rentFreeWithConsent", label: "Rent-free with consent of owner" },
  { key: "rented", label: "Rented" },
  { key: "ownedAmortized", label: "Owned/being amortized" },
];

const numberBuckets: Category[] = [
  { key: "1", label: "1" },
  { key: "2", label: "2" },
  { key: "3", label: "3" },
  { key: "4", label: "4" },
  { key: "5", label: "5" },
  { key: "6", label: "6" },
  { key: "7", label: "7" },
  { key: "8", label: "8" },
  { key: "9", label: "9" },
  { key: "10Plus", label: "10+" },
];

export function getReportDefinitions() {
  return REPORT_DEFINITIONS;
}

export function generateReportData(
  reportId: number,
  options: ReportScopeOptions,
  residents: RbimResidentProfile[],
  households: RbimHouseholdProfile[],
): ReportTableData {
  const definition = getReportDefinition(reportId);
  const scopedResidents = filterResidentsByScope(residents, options);
  const scopedHouseholds = filterHouseholdsByScope(households, options);
  const context: ReportContext = {
    scopeLabel: getScopeLabel(options, scopedResidents, scopedHouseholds),
    residentCount: scopedResidents.length,
    householdCount: scopedHouseholds.length,
  };

  switch (definition.id) {
    case 1:
      return generateTable1PopulationBySexAgeMigration(scopedResidents, context);
    case 2:
      return generateTable2HouseholdSizeHeadSexMigration(scopedHouseholds, context);
    case 3:
      return generateTable3AgeCivilStatusMigration(scopedResidents, context);
    case 4:
      return generateTable4CivilStatusSexMigration(scopedResidents, context);
    case 5:
      return generateTable5ReligionSexMigration(scopedResidents, context);
    case 6:
      return generateTable6EthnicitySexMigration(scopedResidents, context);
    case 7:
      return generateTable7EducationSexMigration(scopedResidents, context);
    case 8:
      return generateTable8EnrollmentSchoolType(scopedResidents, context);
    case 9:
      return generateTable9EnrolledSchoolLevel(scopedResidents, context);
    case 10:
      return generateTable10MonthlyIncome(scopedResidents, context);
    case 11:
      return generateTable11IncomeSource(scopedResidents, context);
    case 12:
      return generateTable12WorkStatus(scopedResidents, context);
    case 13:
      return generateTable13DeliveryPlace(scopedResidents, context);
    case 14:
      return generateTable14BirthAttendant(scopedResidents, context);
    case 15:
      return generateTable15Immunization(scopedResidents, context);
    case 16:
      return generateTable16Gravida(scopedResidents, context);
    case 17:
      return generateTable17LivingChildren(scopedResidents, context);
    case 18:
      return generateTable18ContraceptionUse(scopedResidents, context);
    case 19:
      return generateTable19LivingChildrenContraception(scopedResidents, context);
    case 20:
      return generateTable20FpSource(scopedResidents, context);
    case 21:
      return generateTable21NonFpUsersIntention(scopedResidents, context);
    case 22:
      return generateTable22PreferredFpMethod(scopedResidents, context);
    case 23:
      return generateTable23HealthInsurance(scopedResidents, context);
    case 24:
      return generateTable24HealthFacilityReason(scopedResidents, context);
    case 25:
      return generateTable25HealthFacilityVisited(scopedResidents, context);
    case 26:
      return generateTable26Disability(scopedResidents, context);
    case 27:
      return generateTable27FemaleDeathsByAge(scopedHouseholds, context);
    case 28:
      return generateTable28FemaleDeathsByCause(scopedHouseholds, context);
    case 29:
      return generateTable29ChildDeathsByAgeSex(scopedHouseholds, context);
    case 30:
      return generateTable30ChildDeathsByCauseSex(scopedHouseholds, context);
    case 31:
      return generateTable31CommonDiseasesCauseDeaths(scopedHouseholds, context);
    case 32:
      return generateTable32Parentage(scopedResidents, context);
    case 33:
      return generateTable33SeniorCitizenRegistration(scopedResidents, context);
    case 34:
      return generateTable34VoterRegistration(scopedResidents, context);
    case 35:
      return generateTable35Skills(scopedResidents, context);
    case 36:
      return generateTable36PrimaryNeeds(scopedHouseholds, context);
    case 37:
      return generateTable37TransferYear(scopedResidents, context);
    case 38:
      return generateTable38ReasonLeaving(scopedResidents, context);
    case 39:
      return generateTable39ReasonTransferring(scopedResidents, context);
    case 40:
      return generateTable40IntendedLengthOfStay(scopedResidents, context);
    case 41:
      return generateTable41IntendedResidence(scopedHouseholds, context);
    case 42:
      return generateTable42HousingCharacteristics(scopedHouseholds, context);
    default:
      return generatePlaceholderReport(definition, context);
  }
}

export function generateTable1PopulationBySexAgeMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createSexMigrationColumns("ageGroup", "Age", false);
  const rows = ageGroups0To80.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));

    if (!ageGroup) {
      increment(totals, "invalidResponses");
      increment(totals, "total");
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);
    const columnKey = getSexMigrationColumnKey(migrationStatus, sex);

    if (!row) {
      return;
    }

    if (!columnKey) {
      increment(row, "invalidResponses");
      increment(totals, "invalidResponses");
    } else {
      increment(row, columnKey);
      increment(totals, columnKey);
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(1, context, columns, [...rows, totals], true, [
    "Invalid age responses are included in the Total row under Invalid Responses because the official layout has fixed age rows.",
  ]);
}

export function generateTable2HouseholdSizeHeadSexMigration(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "householdSize", label: "HH number of usual members" },
    ...createMigrationSexGroups(),
    { key: "invalidResponses", label: "Invalid Responses" },
    {
      key: "subTotal",
      label: "Sub Total",
      children: [
        { key: "subTotalMale", label: "Male" },
        { key: "subTotalFemale", label: "Female" },
      ],
    },
    { key: "total", label: "Total" },
  ];
  const sizes = getHouseholdSizeRows(households);
  const rows = sizes.map((size) => createCountRow("householdSize", size, columns));
  const totals = createCountRow("householdSize", "Total", columns, true);
  const rowBySize = mapRowsByLabel(rows, "householdSize");

  households.forEach((household) => {
    const size = String(getHouseholdSize(household) || "1");
    const row = rowBySize.get(size);
    const sex = normalizeSex(getHouseholdHeadSex(household));
    const migrationStatus = normalizeMigrationStatus(getHouseholdHeadMigrationStatus(household));
    const columnKey = getSexMigrationColumnKey(migrationStatus, sex);

    if (!row) {
      return;
    }

    if (!columnKey) {
      increment(row, "invalidResponses");
      increment(totals, "invalidResponses");
    } else {
      const subtotalKey = sex === "Male" ? "subTotalMale" : "subTotalFemale";
      increment(row, columnKey);
      increment(row, subtotalKey);
      increment(totals, columnKey);
      increment(totals, subtotalKey);
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(2, context, columns, [...rows, totals]);
}

export function generateTable3AgeCivilStatusMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createMigrationCategoryColumns("ageGroup", "Age", civilStatusCategories, true);
  const rows = ageGroups0To80.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const civilStatus = normalizeCivilStatus(getResidentCivilStatus(resident));

    if (!ageGroup) {
      increment(totals, "invalidResponses");
      increment(totals, "total");
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    if (migrationStatus === "Invalid") {
      increment(row, "invalidResponses");
      increment(totals, "invalidResponses");
    } else {
      const key = `${getMigrationKey(migrationStatus)}_${civilStatus.key}`;
      increment(row, key);
      increment(totals, key);
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(3, context, columns, [...rows, totals]);
}

export function generateTable4CivilStatusSexMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createSexMigrationColumns("civilStatus", "Marital Status", true);
  const rows = civilStatusCategories.map((category) => createCountRow("civilStatus", category.label, columns));
  const totals = createCountRow("civilStatus", "Total", columns, true);
  const rowByStatus = mapRowsByLabel(rows, "civilStatus");

  residents.forEach((resident) => {
    const civilStatus = normalizeCivilStatus(getResidentCivilStatus(resident));
    const row = rowByStatus.get(civilStatus.label);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const columnKey = getSexMigrationColumnKey(migrationStatus, sex);

    if (!row) {
      return;
    }

    if (!columnKey) {
      increment(row, "invalidResponses");
      increment(totals, "invalidResponses");
    } else {
      increment(row, columnKey);
      increment(totals, columnKey);
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(4, context, columns, [...rows, totals]);
}

export function generateTable5ReligionSexMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateDynamicSexMigrationReport({
    reportId: 5,
    labelKey: "religion",
    label: "Religion",
    residents,
    context,
    getValue: getResidentReligion,
    normalizeValue: normalizeReligion,
    preferredOrder: ["BAPTIST", "IFI", "NONE", "PENTECOSTAL", "PMAI", "RC", "SDA", "SPA"],
  });
}

export function generateTable6EthnicitySexMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateDynamicSexMigrationReport({
    reportId: 6,
    labelKey: "ethnicity",
    label: "Ethnicity",
    residents,
    context,
    getValue: getResidentEthnicity,
    normalizeValue: normalizeEthnicity,
    preferredOrder: ["BISAYA", "ENGLISH", "HIGAONON"],
  });
}

export function generateTable7EducationSexMigration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantSexCategoryColumns("ageGroup", "Age", educationCategories, true);
  const rows = ageGroups5AndOverEducation.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getEducationAgeGroup);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const education = normalizeEducationLevel(getResidentEducationLevel(resident));

    if (!ageGroup || sex === "Invalid" || migrationStatus !== "Non-Migrant" || !education) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);
    const sexKey = getSexKey(sex);

    if (!row || !sexKey) {
      return;
    }

    increment(row, `nonMigrant_${education.key}_${sexKey}`);
    increment(row, `nonMigrantSubTotal_${sexKey}`);
    increment(row, "total");
    increment(totals, `nonMigrant_${education.key}_${sexKey}`);
    increment(totals, `nonMigrantSubTotal_${sexKey}`);
    increment(totals, "total");
  });

  return buildReportTable(7, context, columns, [...rows, totals]);
}

export function generateTable8EnrollmentSchoolType(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 8,
    rows: ageGroups3To24,
    getAgeGroup: getAgeGroup3To24,
    categories: enrollmentCategories,
    residents,
    context,
    getValue: getResidentEnrollmentStatus,
    normalizeValue: normalizeEnrollmentStatus,
    includeResident: (resident) => isResidentAgeInRange(resident, 3, 24),
  });
}

export function generateTable9EnrolledSchoolLevel(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 9,
    rows: ageGroups3To24,
    getAgeGroup: getAgeGroup3To24,
    categories: schoolLevelCategories,
    residents,
    context,
    getValue: getResidentSchoolLevel,
    normalizeValue: normalizeSchoolLevel,
    includeResident: (resident) =>
      isResidentAgeInRange(resident, 3, 24) &&
      normalizeEnrollmentStatus(getResidentEnrollmentStatus(resident))?.key !== "notEnrolled",
  });
}

export function generateTable10MonthlyIncome(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 10,
    rows: ageGroups15AndOver,
    getAgeGroup: getAgeGroup15AndOver,
    categories: incomeCategories,
    residents,
    context,
    getValue: getResidentMonthlyIncome,
    normalizeValue: normalizeIncomeRange,
    includeResident: (resident) => isResidentAgeInRange(resident, 15, Number.POSITIVE_INFINITY),
  });
}

export function generateTable11IncomeSource(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 11,
    rows: ageGroups15AndOver,
    getAgeGroup: getAgeGroup15AndOver,
    categories: incomeSourceCategories,
    residents,
    context,
    getValue: getResidentIncomeSource,
    normalizeValue: normalizeIncomeSource,
    includeResident: (resident) => isResidentAgeInRange(resident, 15, Number.POSITIVE_INFINITY),
  });
}

export function generateTable12WorkStatus(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 12,
    rows: ageGroups15AndOver,
    getAgeGroup: getAgeGroup15AndOver,
    categories: workStatusCategories,
    residents,
    context,
    getValue: getResidentWorkStatus,
    normalizeValue: normalizeWorkStatus,
    includeResident: (resident) => isResidentAgeInRange(resident, 15, Number.POSITIVE_INFINITY),
  });
}

export function generateTable13DeliveryPlace(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateInfantSexReport({
    reportId: 13,
    labelKey: "placeOfDelivery",
    label: "Place of Delivery",
    rows: deliveryPlaceCategories,
    residents,
    context,
    getValue: getResidentPlaceOfDelivery,
    normalizeValue: normalizeDeliveryPlace,
  });
}

export function generateTable14BirthAttendant(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateInfantSexReport({
    reportId: 14,
    labelKey: "birthAttendant",
    label: "Birth Attendant",
    rows: birthAttendantCategories,
    residents,
    context,
    getValue: getResidentBirthAttendant,
    normalizeValue: normalizeBirthAttendant,
  });
}

export function generateTable15Immunization(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const infantResidents = residents.filter(isInfantUnderOneYear);
  const dynamicRows = getOrderedCategories(
    infantResidents
      .map((resident) => normalizeImmunization(getResidentLastImmunization(resident)))
      .filter((category): category is Category => Boolean(category)),
    [],
  );

  return generateInfantSexReport({
    reportId: 15,
    labelKey: "immunization",
    label: "Immunization",
    rows: dynamicRows.length ? dynamicRows : [{ key: "none", label: "None" }],
    residents,
    context,
    getValue: getResidentLastImmunization,
    normalizeValue: normalizeImmunization,
  });
}

export function generateTable16Gravida(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateFemaleAgeMigrationNumberReport({
    reportId: 16,
    residents,
    context,
    getValue: getResidentGravida,
  });
}

export function generateTable17LivingChildren(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateFemaleAgeMigrationNumberReport({
    reportId: 17,
    residents,
    context,
    getValue: getResidentLivingChildren,
  });
}

export function generateTable18ContraceptionUse(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantCategoryColumns("ageGroup", "Age", contraceptionCategories);
  const rows = ageGroups10To54.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup10To54);
    const method = normalizeContraceptionMethod(getResidentContraceptionMethod(resident));

    if (!isFemaleNonMigrantInAgeRange(resident, 10, 54) || !ageGroup || !method) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `nonMigrant_${method.key}`);
    increment(totals, `nonMigrant_${method.key}`);
  });

  return buildReportTable(18, context, columns, [...rows, totals]);
}

export function generateTable19LivingChildrenContraception(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantCategoryColumns("livingChildren", "Number of Living Children", contraceptionCategories);
  const rows = numberBuckets.map((bucket) => createCountRow("livingChildren", bucket.label, columns));
  const totals = createCountRow("livingChildren", "Total", columns, true);
  const rowByLivingChildren = mapRowsByLabel(rows, "livingChildren");

  residents.forEach((resident) => {
    const livingChildren = normalizeLivingChildren(getResidentLivingChildren(resident));
    const method = normalizeContraceptionMethod(getResidentContraceptionMethod(resident));

    if (!isFemaleNonMigrantInAgeRange(resident, 10, 54) || !livingChildren || !method) {
      return;
    }

    const row = rowByLivingChildren.get(livingChildren.label);

    if (!row) {
      return;
    }

    increment(row, `nonMigrant_${method.key}`);
    increment(totals, `nonMigrant_${method.key}`);
  });

  return buildReportTable(19, context, columns, [...rows, totals]);
}

export function generateTable20FpSource(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantCategoryColumns("ageGroup", "Age", fpSourceCategories);
  const rows = ageGroups10To54.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup10To54);
    const method = normalizeContraceptionMethod(getResidentContraceptionMethod(resident));
    const source = normalizeFpSource(getResidentFpSource(resident));

    if (!isFemaleNonMigrantInAgeRange(resident, 10, 54) || !ageGroup || !source || !isModernFpMethod(method)) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `nonMigrant_${source.key}`);
    increment(totals, `nonMigrant_${source.key}`);
  });

  return buildReportTable(20, context, columns, [...rows, totals]);
}

export function generateTable21NonFpUsersIntention(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createMigrationCategoryColumns("ageGroup", "Age", fpIntentionCategories, false, true);
  const rows = ageGroups10To54.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup10To54);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const currentMethod = normalizeFpMethod(getResidentFpCurrentUse(resident));
    const intention = normalizeFpIntention(getResidentFpIntention(resident));
    const migrationKey = getMigrationKey(migrationStatus);

    if (sex !== "Female" || currentMethod?.key !== "none" || !ageGroup || !intention || !migrationKey) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `${migrationKey}_${intention.key}`);
    increment(row, `total_${intention.key}`);
    increment(totals, `${migrationKey}_${intention.key}`);
    increment(totals, `total_${intention.key}`);
  });

  return buildReportTable(21, context, columns, [...rows, totals]);
}

export function generateTable22PreferredFpMethod(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createMigrationTotalColumns("fpMethod", "Family Planning Methods");
  const rows = preferredFpMethodCategories.map((category) => createCountRow("fpMethod", category.label, columns));
  const totals = createCountRow("fpMethod", "Total", columns, true);
  const rowByMethod = mapRowsByLabel(rows, "fpMethod");

  residents.forEach((resident) => {
    const currentMethod = normalizeFpMethod(getResidentFpCurrentUse(resident));
    const intention = normalizeFpIntention(getResidentFpIntention(resident));
    const preferredMethod = normalizePreferredFpMethod(getResidentPreferredFpMethod(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const migrationKey = getMigrationKey(migrationStatus);

    if (
      normalizeSex(getResidentSex(resident)) !== "Female" ||
      !isResidentAgeInRange(resident, 10, 54) ||
      currentMethod?.key !== "none" ||
      intention?.key !== "withIntention" ||
      !preferredMethod ||
      !migrationKey
    ) {
      return;
    }

    const row = rowByMethod.get(preferredMethod.label);

    if (!row) {
      return;
    }

    increment(row, migrationKey);
    increment(row, "total");
    increment(totals, migrationKey);
    increment(totals, "total");
  });

  return buildReportTable(22, context, columns, [...rows, totals]);
}

export function generateTable23HealthInsurance(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantSexCategoryColumns("ageGroup", "Age", healthInsuranceCategories, false, false);
  const rows = ageGroups0To80.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const healthInsurance = normalizeHealthInsurance(getResidentHealthInsurance(resident));
    const sexKey = getSexKey(sex);

    if (!ageGroup || migrationStatus !== "Non-Migrant" || !healthInsurance || !sexKey) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `nonMigrant_${healthInsurance.key}_${sexKey}`);
    increment(totals, `nonMigrant_${healthInsurance.key}_${sexKey}`);
  });

  return buildReportTable(23, context, columns, [...rows, totals]);
}

export function generateTable24HealthFacilityReason(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createMigrationSexTotalColumns("reason", "Reason");
  const rows = healthFacilityReasonCategories.map((category) => createCountRow("reason", category.label, columns));
  const totals = createCountRow("reason", "Total", columns, true);
  const rowByReason = mapRowsByLabel(rows, "reason");

  residents.forEach((resident) => {
    const facility = normalizeHealthFacilityType(getResidentHealthFacilityVisited(resident));
    const reason = normalizeHealthFacilityReason(getResidentHealthFacilityReason(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const sex = normalizeSex(getResidentSex(resident));
    const migrationKey = getMigrationKey(migrationStatus);
    const sexKey = getSexKey(sex);

    if (!facility || !reason || !migrationKey || !sexKey) {
      return;
    }

    const row = rowByReason.get(reason.label);

    if (!row) {
      return;
    }

    increment(row, `${migrationKey}${capitalize(sexKey)}`);
    increment(row, `total${capitalize(sexKey)}`);
    increment(totals, `${migrationKey}${capitalize(sexKey)}`);
    increment(totals, `total${capitalize(sexKey)}`);
  });

  return buildReportTable(24, context, columns, [...rows, totals]);
}

export function generateTable25HealthFacilityVisited(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 25,
    rows: ageGroups0To80,
    getAgeGroup: getAgeBracket,
    categories: healthFacilityTypeCategories,
    residents,
    context,
    getValue: getResidentHealthFacilityVisited,
    normalizeValue: normalizeHealthFacilityType,
    includeResident: () => true,
  });
}

export function generateTable26Disability(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 26,
    rows: ageGroups0To80,
    getAgeGroup: getAgeBracket,
    categories: disabilityTypeCategories,
    residents,
    context,
    getValue: getResidentDisabilityType,
    normalizeValue: normalizeDisabilityType,
    includeResident: (resident) => Boolean(normalizeDisabilityType(getResidentDisabilityStatus(resident))),
  });
}

export function generateTable27FemaleDeathsByAge(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "ageGroup", label: "Age" },
    { key: "femaleMortality", label: "Number of Female Mortality" },
  ];
  const rows = ageGroups0To80.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  getReportedDeaths(households)
    .filter((death) => normalizeSex(getDeathSex(death)) === "Female")
    .forEach((death) => {
      const ageGroup = getAgeBracket(getDeathAge(death));
      const row = ageGroup ? rowByAgeGroup.get(ageGroup) : null;

      if (!row) {
        return;
      }

      increment(row, "femaleMortality");
      increment(totals, "femaleMortality");
    });

  return buildReportTable(27, context, columns, [...rows, totals]);
}

export function generateTable28FemaleDeathsByCause(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const deaths = getReportedDeaths(households).filter((death) => normalizeSex(getDeathSex(death)) === "Female");

  return generateCauseCountReport({
    reportId: 28,
    labelKey: "causeOfDeath",
    label: "Cause of Death",
    deaths,
    context,
    bySex: false,
  });
}

export function generateTable29ChildDeathsByAgeSex(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "age", label: "Age" },
    { key: "male", label: "Male" },
    { key: "female", label: "Female" },
  ];
  const rows = ["0", "1", "2", "3", "4", "5"].map((age) => createCountRow("age", age, columns));
  const totals = createCountRow("age", "Total", columns, true);
  const rowByAge = mapRowsByLabel(rows, "age");

  getReportedDeaths(households)
    .filter((death) => {
      const age = getDeathAge(death);

      return isValidAge(age) && age <= 5;
    })
    .forEach((death) => {
      const sexKey = getSexKey(normalizeSex(getDeathSex(death)));
      const row = rowByAge.get(String(getDeathAge(death)));

      if (!row || !sexKey) {
        return;
      }

      increment(row, sexKey);
      increment(totals, sexKey);
    });

  return buildReportTable(29, context, columns, [...rows, totals]);
}

export function generateTable30ChildDeathsByCauseSex(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const deaths = getReportedDeaths(households).filter((death) => {
    const age = getDeathAge(death);

    return isValidAge(age) && age <= 5;
  });

  return generateCauseCountReport({
    reportId: 30,
    labelKey: "reason",
    label: "Reason",
    deaths,
    context,
    bySex: true,
  });
}

export function generateTable31CommonDiseasesCauseDeaths(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "reason", label: "Reason" },
    { key: "total", label: "Total" },
  ];
  const causes = [
    ...getReportedDeaths(households).map((death) => normalizeCauseOfDeath(getDeathCause(death))),
    ...households.flatMap(getHouseholdCommonDiseaseCauses).map(normalizeCauseOfDeath),
  ].filter((cause): cause is Category => Boolean(cause));
  const categories = getOrderedCategories(causes, ["ACCIDENT", "DENGUE", "HIGH BLOOD", "HILANT", "TUBIG"]);
  const rows = (categories.length ? categories : [{ key: "none", label: "NONE" }]).map((category) =>
    createCountRow("reason", category.label, columns),
  );
  const totals = createCountRow("reason", "Total", columns, true);
  const rowByReason = mapRowsByLabel(rows, "reason");

  causes.forEach((cause) => {
    const row = rowByReason.get(cause.label);

    if (!row) {
      return;
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(31, context, columns, [...rows, totals]);
}

export function generateTable32Parentage(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 32,
    rows: ageGroups10To54,
    getAgeGroup: getAgeGroup10To54,
    categories: parentageCategories,
    residents,
    context,
    getValue: getResidentParentageStatus,
    normalizeValue: normalizeParentageStatus,
    includeResident: (resident) =>
      isResidentAgeInRange(resident, 10, 54) &&
      Boolean(normalizeParentageStatus(getResidentParentageStatus(resident))),
  });
}

export function generateTable33SeniorCitizenRegistration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createMigrationCategoryColumns("sex", "Sex", seniorCitizenCategories, false, true);
  const rows = ["Male", "Female"].map((sex) => createCountRow("sex", sex, columns));
  const totals = createCountRow("sex", "Total", columns, true);
  const rowBySex = mapRowsByLabel(rows, "sex");

  residents
    .filter((resident) => isResidentAgeInRange(resident, 60, Number.POSITIVE_INFINITY))
    .forEach((resident) => {
      const sex = normalizeSex(getResidentSex(resident));
      const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
      const seniorStatus = normalizeSeniorCitizenRegistration(getResidentSeniorCitizenStatus(resident));
      const migrationKey = getMigrationKey(migrationStatus);
      const row = rowBySex.get(sex);

      if (!row || !seniorStatus || !migrationKey) {
        return;
      }

      increment(row, `${migrationKey}_${seniorStatus.key}`);
      increment(row, `total_${seniorStatus.key}`);
      increment(totals, `${migrationKey}_${seniorStatus.key}`);
      increment(totals, `total_${seniorStatus.key}`);
    });

  return buildReportTable(33, context, columns, [...rows, totals]);
}

export function generateTable34VoterRegistration(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleAgeMigrationCategoryReport({
    reportId: 34,
    rows: ageGroups15AndOver,
    getAgeGroup: getAgeGroup15AndOver,
    categories: voterCategories,
    residents,
    context,
    getValue: getResidentVoterRegistrationStatus,
    normalizeValue: normalizeVoterRegistration,
    includeResident: (resident) => isResidentAgeInRange(resident, 15, Number.POSITIVE_INFINITY),
  });
}

export function generateTable35Skills(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = createNonMigrantSexCategoryColumns("ageGroup", "Age", skillCategories, false, false);
  const rows = ageGroups15AndOver.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup15AndOver);
    const sex = normalizeSex(getResidentSex(resident));
    const sexKey = getSexKey(sex);
    const skills = getResidentSkills(resident).map(normalizeSkill).filter((skill): skill is Category => Boolean(skill));

    if (!ageGroup || normalizeMigrationStatus(getResidentMigrationStatus(resident)) !== "Non-Migrant" || !sexKey) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    skills.forEach((skill) => {
      increment(row, `nonMigrant_${skill.key}_${sexKey}`);
      increment(totals, `nonMigrant_${skill.key}_${sexKey}`);
    });
  });

  return buildReportTable(35, context, columns, [...rows, totals]);
}

export function generateTable36PrimaryNeeds(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "primaryNeed", label: "Primary Needs of the Barangay" },
    { key: "total", label: "Total Household response" },
  ];
  const needs = households.flatMap(getHouseholdPrimaryNeeds).map(normalizePrimaryNeed).filter((need): need is Category => Boolean(need));
  const categories = getOrderedCategories(needs, ["GREAT LEADERSHIP", "JOB", "LIBRE TAMBAL", "MAAYONG PAGLANTAW SA KAUGMAON", "TRABAHO", "TUBIG", "WATER"]);
  const rows = (categories.length ? categories : [{ key: "none", label: "NONE" }]).map((category) =>
    createCountRow("primaryNeed", category.label, columns),
  );
  const totals = createCountRow("primaryNeed", "Total", columns, true);
  const rowByNeed = mapRowsByLabel(rows, "primaryNeed");

  needs.forEach((need) => {
    const row = rowByNeed.get(need.label);

    if (!row) {
      return;
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(36, context, columns, [...rows, totals]);
}

export function generateTable37TransferYear(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "ageGroup", label: "Age" },
    {
      key: "male",
      label: "Male",
      children: [
        {
          key: "male_migrants",
          label: "Migrants",
          children: transferYearCategories.map((category) => ({
            key: `male_migrants_${category.key}`,
            label: category.label,
          })),
        },
      ],
    },
  ];
  const rowLabels = ageGroups0To80.filter((ageGroup) => ageGroup !== "0-4");
  const rows = rowLabels.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const transferYear = normalizeTransferYear(getResidentTransferYear(resident));

    if (normalizeSex(getResidentSex(resident)) !== "Male" || migrationStatus === "Non-Migrant" || migrationStatus === "Invalid" || !ageGroup || !transferYear) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `male_migrants_${transferYear.key}`);
    increment(totals, `male_migrants_${transferYear.key}`);
  });

  return buildReportTable(37, context, columns, [...rows, totals]);
}

export function generateTable38ReasonLeaving(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleMigrantReasonReport({
    reportId: 38,
    categories: reasonLeavingCategories,
    residents,
    context,
    getValues: getResidentReasonLeavingPreviousBarangay,
    normalizeValue: normalizeReasonLeavingPreviousBarangay,
  });
}

export function generateTable39ReasonTransferring(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  return generateMaleMigrantReasonReport({
    reportId: 39,
    categories: reasonTransferringCategories,
    residents,
    context,
    getValues: getResidentReasonTransferringCurrentBarangay,
    normalizeValue: normalizeReasonTransferringCurrentBarangay,
  });
}

export function generateTable40IntendedLengthOfStay(
  residents: RbimResidentProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "ageGroup", label: "Age" },
    {
      key: "male",
      label: "Male",
      children: [
        {
          key: "male_migrant",
          label: "Migrants",
          children: intendedLengthOfStayCategories.map((category) => ({
            key: `male_migrant_${category.key}`,
            label: category.label,
          })),
        },
        {
          key: "male_transient",
          label: "Transients",
          children: intendedLengthOfStayCategories.map((category) => ({
            key: `male_transient_${category.key}`,
            label: category.label,
          })),
        },
      ],
    },
  ];
  const rowLabels = ageGroups0To80.filter((ageGroup) => ageGroup !== "0-4");
  const rows = rowLabels.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const migrationKey = getMigrationKey(migrationStatus);
    const intendedStay = normalizeIntendedLengthOfStay(getResidentIntendedLengthOfStay(resident));

    if (
      normalizeSex(getResidentSex(resident)) !== "Male" ||
      !ageGroup ||
      !migrationKey ||
      migrationStatus === "Non-Migrant" ||
      !intendedStay
    ) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    increment(row, `male_${migrationKey}_${intendedStay.key}`);
    increment(totals, `male_${migrationKey}_${intendedStay.key}`);
  });

  return buildReportTable(40, context, columns, [...rows, totals]);
}

export function generateTable41IntendedResidence(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "intendedResidence", label: "Intends to stay 5 years from now" },
    { key: "total", label: "Total Household response" },
  ];
  const rows = intendedResidenceCategories.map((category) => createCountRow("intendedResidence", category.label, columns));
  const totals = createCountRow("intendedResidence", "Total", columns, true);
  const rowByResidence = mapRowsByLabel(rows, "intendedResidence");
  const countedHouseholds = new Set<string>();

  households.forEach((household) => {
    const householdId = getHouseholdId(household);

    if (countedHouseholds.has(householdId)) {
      return;
    }

    const residence = normalizeIntendedResidenceFuture(
      getHouseholdIntendedResidenceFuture(household),
      getHouseholdBarangay(household),
    );
    const row = residence ? rowByResidence.get(residence.label) : null;

    if (!row) {
      return;
    }

    countedHouseholds.add(householdId);
    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(41, context, columns, [...rows, totals]);
}

export function generateTable42HousingCharacteristics(
  households: RbimHouseholdProfile[],
  context: ReportContext,
): ReportTableData {
  const columns = [
    { key: "housingCharacteristics", label: "Housing Characteristics" },
    { key: "total", label: "Total Household response" },
  ];
  const fixedRows = housingCharacteristicCategories.map((category) =>
    createCountRow("housingCharacteristics", category.label, columns),
  );
  const dynamicCategories = getOrderedCategories(
    households
      .map((household) => normalizeHousingCharacteristics(getHouseholdHousingCharacteristics(household)))
      .filter((category): category is Category => Boolean(category))
      .filter((category) => !housingCharacteristicCategories.some((fixed) => fixed.key === category.key)),
    [],
  );
  const dynamicRows = dynamicCategories.map((category) => createCountRow("housingCharacteristics", category.label, columns));
  const sectionRow: ReportRow = {
    housingCharacteristics: "Ownership of Housing Unit",
    total: undefined,
  };
  const rows = [sectionRow, ...fixedRows, ...dynamicRows];
  const totals = createCountRow("housingCharacteristics", "Total", columns, true);
  const rowByCharacteristic = mapRowsByLabel([...fixedRows, ...dynamicRows], "housingCharacteristics");
  const countedHouseholds = new Set<string>();

  households.forEach((household) => {
    const householdId = getHouseholdId(household);

    if (countedHouseholds.has(householdId)) {
      return;
    }

    const characteristic = normalizeHousingCharacteristics(getHouseholdHousingCharacteristics(household));
    const row = characteristic ? rowByCharacteristic.get(characteristic.label) : null;

    if (!row) {
      return;
    }

    countedHouseholds.add(householdId);
    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(42, context, columns, [...rows, totals]);
}

export function filterResidentsByScope(records: RbimResidentProfile[], options: ReportScopeOptions) {
  if (options.municipalityWide) {
    const municipality = normalizeComparable(options.municipality);

    return municipality
      ? records.filter((record) => normalizeComparable(getResidentCityMunicipality(record)) === municipality)
      : records;
  }

  const barangay = normalizeComparable(options.barangay);

  if (!barangay) {
    return records;
  }

  return records.filter((record) => normalizeComparable(getResidentBarangay(record)) === barangay);
}

export function filterHouseholdsByScope(records: RbimHouseholdProfile[], options: ReportScopeOptions) {
  if (options.municipalityWide) {
    const municipality = normalizeComparable(options.municipality);

    return municipality
      ? records.filter((record) => normalizeComparable(record.cityMunicipality) === municipality)
      : records;
  }

  const barangay = normalizeComparable(options.barangay);

  if (!barangay) {
    return records;
  }

  return records.filter((record) => normalizeComparable(record.barangay) === barangay);
}

export function flattenReportColumns(columns: ReportColumn[]): ReportColumn[] {
  return columns.flatMap((column) => column.children?.length ? flattenReportColumns(column.children) : [column]);
}

export function getReportColumnLeafCount(column: ReportColumn): number {
  return column.children?.length
    ? column.children.reduce((sum, child) => sum + getReportColumnLeafCount(child), 0)
    : 1;
}

export function getReportColumnDepth(columns: ReportColumn[]): number {
  return Math.max(1, ...columns.map(getSingleColumnDepth));
}

export function buildReportHeaderRows(columns: ReportColumn[]) {
  const depth = getReportColumnDepth(columns);
  const leafCount = flattenReportColumns(columns).length;
  const rows = Array.from({ length: depth }, () => Array.from({ length: leafCount }, () => ""));

  fillHeaderRows(columns, rows, 0, 0);

  return rows;
}

export function getAgeBracket(ageYears: number | null | undefined) {
  if (!isValidAge(ageYears)) {
    return null;
  }

  if (ageYears >= 80) {
    return "80+";
  }

  const start = Math.floor(ageYears / 5) * 5;
  const end = start + 4;

  return `${start}-${end}`;
}

export function normalizeSex(value: unknown): Sex {
  const normalizedValue = normalizeComparable(value);

  if (["1", "m", "male"].includes(normalizedValue)) {
    return "Male";
  }

  if (["2", "f", "female"].includes(normalizedValue)) {
    return "Female";
  }

  return "Invalid";
}

export function normalizeMigrationStatus(value: unknown): MigrationStatus {
  const normalizedValue = normalizeComparable(value).replace(/-/g, " ");

  if (["1", "non migrant", "nonmigrant", "non migrant resident"].includes(normalizedValue)) {
    return "Non-Migrant";
  }

  if (["2", "migrant"].includes(normalizedValue)) {
    return "Migrant";
  }

  if (["3", "transient"].includes(normalizedValue)) {
    return "Transient";
  }

  return "Invalid";
}

export function normalizeCivilStatus(value: unknown): Category {
  const cleaned = cleanCodedValue("q08_marital_status", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("MARRIED") && !normalized.includes("COMMON")) {
    return civilStatusCategories[1];
  }

  if (normalized.includes("COMMON") || normalized.includes("LIVE IN") || normalized.includes("LIVING IN")) {
    return civilStatusCategories[2];
  }

  if (normalized.includes("WIDOW")) {
    return civilStatusCategories[3];
  }

  if (normalized.includes("SEPARATED")) {
    return civilStatusCategories[4];
  }

  if (normalized.includes("DIVORCED")) {
    return civilStatusCategories[5];
  }

  if (normalized.includes("SINGLE") || normalized === "1") {
    return civilStatusCategories[0];
  }

  return civilStatusCategories[6];
}

export function normalizeReligion(value: unknown): Category {
  const normalized = normalizeStandardKey(value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return { key: "none", label: "NONE" };
  }

  if (normalized.includes("ROMAN CATHOLIC") || normalized === "RC" || normalized.includes("CATHOLIC")) {
    return { key: "rc", label: "RC" };
  }

  if (normalized.includes("BAPTIST")) {
    return { key: "baptist", label: "BAPTIST" };
  }

  if (normalized.includes("IGLESIA FILIPINA") || normalized === "IFI") {
    return { key: "ifi", label: "IFI" };
  }

  if (normalized.includes("PENTECOSTAL")) {
    return { key: "pentecostal", label: "PENTECOSTAL" };
  }

  if (normalized === "PMAI" || normalized.includes("PHILIPPINE MISSIONARY") || normalized.includes("PBMA")) {
    return { key: "pmai", label: "PMAI" };
  }

  if (normalized.includes("SEVENTH DAY") || normalized === "SDA") {
    return { key: "sda", label: "SDA" };
  }

  if (normalized === "SPA") {
    return { key: "spa", label: "SPA" };
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function normalizeEthnicity(value: unknown): Category {
  const normalized = normalizeStandardKey(value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return { key: "unknown", label: "UNKNOWN" };
  }

  if (normalized.includes("BISAYA") || normalized.includes("VISAYA")) {
    return { key: "bisaya", label: "BISAYA" };
  }

  if (normalized.includes("HIGAONON") || normalized.includes("HIGA ONON")) {
    return { key: "higaonon", label: "HIGAONON" };
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function normalizeEducationLevel(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q11_highest_education", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("NO GRADE") || normalized.includes("NO EDUCATION") || normalized === "NONE" || normalized === "0") {
    return educationCategories[0];
  }

  if (normalized.includes("EARLY CHILDHOOD") || normalized.includes("PRE SCHOOL") || normalized.includes("PRESCHOOL")) {
    return educationCategories[1];
  }

  if (normalized.includes("ELEMENTARY GRAD")) {
    return educationCategories[3];
  }

  if (normalized.includes("PRIMARY") || normalized.includes("ELEMENTARY")) {
    return educationCategories[2];
  }

  if (normalized.includes("HIGH SCHOOL GRAD")) {
    return educationCategories[5];
  }

  if (normalized.includes("HIGH SCHOOL")) {
    return educationCategories[4];
  }

  if (normalized.includes("JUNIOR") && normalized.includes("GRAD")) {
    return educationCategories[7];
  }

  if (normalized.includes("LOWER SECONDARY") || normalized.includes("JUNIOR")) {
    return educationCategories[6];
  }

  if (normalized.includes("SENIOR") && normalized.includes("GRAD")) {
    return educationCategories[9];
  }

  if (normalized.includes("UPPER SECONDARY") || normalized.includes("SENIOR")) {
    return educationCategories[8];
  }

  if (normalized.includes("VOCATIONAL") || normalized.includes("TECHNICAL") || normalized.includes("POST SECONDARY") || normalized.includes("SHORT CYCLE")) {
    return educationCategories[10];
  }

  if (normalized.includes("COLLEGE GRAD") || normalized.includes("BACHELOR")) {
    return educationCategories[12];
  }

  if (normalized.includes("COLLEGE")) {
    return educationCategories[11];
  }

  if (normalized.includes("MASTER") || normalized.includes("DOCTOR")) {
    return educationCategories[13];
  }

  return null;
}

export function normalizeEnrollmentStatus(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q12_currently_enrolled", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("PUBLIC")) {
    return enrollmentCategories[0];
  }

  if (normalized.includes("PRIVATE")) {
    return enrollmentCategories[1];
  }

  if (normalized === "3" || normalized === "NO" || normalized.includes("NOT ENROLLED")) {
    return enrollmentCategories[2];
  }

  return null;
}

export function normalizeSchoolType(value: unknown): Category | null {
  return normalizeEnrollmentStatus(value);
}

export function normalizeSchoolLevel(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q13_school_level", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("EARLY CHILDHOOD") || normalized.includes("PRE SCHOOL") || normalized.includes("PRESCHOOL")) {
    return schoolLevelCategories[0];
  }

  if (normalized.includes("PRIMARY") || normalized.includes("ELEMENTARY")) {
    return schoolLevelCategories[1];
  }

  if (normalized.includes("LOWER SECONDARY") || normalized.includes("JUNIOR")) {
    return schoolLevelCategories[2];
  }

  if (normalized.includes("UPPER SECONDARY") || normalized.includes("SENIOR")) {
    return schoolLevelCategories[3];
  }

  if (normalized.includes("VOCATIONAL") || normalized.includes("TECHNICAL") || normalized.includes("POST SECONDARY") || normalized.includes("SHORT CYCLE")) {
    return schoolLevelCategories[4];
  }

  if (normalized.includes("BACHELOR") || normalized.includes("COLLEGE") || normalized.includes("UNIVERSITY")) {
    return schoolLevelCategories[5];
  }

  return null;
}

export function normalizeIncomeRange(value: unknown): Category | null {
  const normalized = String(value ?? "").replace(/,/g, "").trim();
  const numericMatch = normalized.match(/-?\d+(?:\.\d+)?/);

  if (!numericMatch) {
    return null;
  }

  const amount = Number(numericMatch[0]);

  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }

  if (amount < 5000) {
    return incomeCategories[0];
  }

  if (amount < 10000) {
    return incomeCategories[1];
  }

  if (amount < 15000) {
    return incomeCategories[2];
  }

  if (amount < 20000) {
    return incomeCategories[3];
  }

  if (amount < 25000) {
    return incomeCategories[4];
  }

  return incomeCategories[5];
}

export function normalizeIncomeSource(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q17_source_of_income", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("EMPLOY")) {
    return incomeSourceCategories[0];
  }

  if (normalized.includes("BUSINESS")) {
    return incomeSourceCategories[1];
  }

  if (normalized.includes("REMITTANCE")) {
    return incomeSourceCategories[2];
  }

  if (normalized.includes("INVEST")) {
    return incomeSourceCategories[3];
  }

  if (normalized.includes("OTHER")) {
    return incomeSourceCategories[4];
  }

  return null;
}

export function normalizeWorkStatus(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q18_work_status", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("PERMANENT")) {
    return workStatusCategories[0];
  }

  if (normalized.includes("CASUAL")) {
    return workStatusCategories[1];
  }

  if (normalized.includes("CONTRACTUAL")) {
    return workStatusCategories[2];
  }

  if (normalized.includes("INDIVIDUALLY") || normalized.includes("OWNED BUSINESS")) {
    return workStatusCategories[3];
  }

  if (normalized.includes("SHARED") || normalized.includes("PARTNERSHIP")) {
    return workStatusCategories[4];
  }

  if (normalized.includes("CORPORATE")) {
    return workStatusCategories[5];
  }

  return null;
}

export function normalizeDeliveryPlace(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q20_place_of_delivery", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("PUBLIC") && normalized.includes("HOSPITAL")) {
    return deliveryPlaceCategories[0];
  }

  if (normalized.includes("PRIVATE") && normalized.includes("HOSPITAL")) {
    return deliveryPlaceCategories[1];
  }

  if (normalized.includes("LYING")) {
    return deliveryPlaceCategories[2];
  }

  if (normalized.includes("HOME")) {
    return deliveryPlaceCategories[3];
  }

  if (normalized.includes("OTHER")) {
    return deliveryPlaceCategories[4];
  }

  return null;
}

export function normalizeBirthAttendant(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q21_birth_attendant", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("DOCTOR")) {
    return birthAttendantCategories[0];
  }

  if (normalized.includes("NURSE")) {
    return birthAttendantCategories[1];
  }

  if (normalized.includes("MIDWIFE")) {
    return birthAttendantCategories[2];
  }

  if (normalized.includes("HILOT")) {
    return birthAttendantCategories[3];
  }

  if (normalized.includes("OTHER")) {
    return birthAttendantCategories[4];
  }

  return null;
}

export function normalizeImmunization(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q22_immunization", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function normalizeGravida(value: unknown): Category | null {
  return normalizeNumberBucket(value);
}

export function normalizeLivingChildren(value: unknown): Category | null {
  return normalizeNumberBucket(value);
}

export function normalizeContraceptionMethod(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q24_family_planning_use", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized === "0" || normalized === "NONE") {
    return contraceptionCategories[9];
  }

  if (normalized.includes("FEMALE STERILIZATION") || normalized.includes("LIGATION")) {
    return contraceptionCategories[0];
  }

  if (normalized.includes("MALE STERILIZATION") || normalized.includes("VASECTOMY")) {
    return contraceptionCategories[1];
  }

  if (normalized.includes("IUD")) {
    return contraceptionCategories[2];
  }

  if (normalized.includes("INJECT")) {
    return contraceptionCategories[3];
  }

  if (normalized.includes("IMPLANT")) {
    return contraceptionCategories[4];
  }

  if (normalized.includes("PILL")) {
    return contraceptionCategories[5];
  }

  if (normalized.includes("CONDOM")) {
    return contraceptionCategories[6];
  }

  if (normalized.includes("MODERN NATURAL")) {
    return contraceptionCategories[7];
  }

  if (normalized.includes("LAM") || normalized.includes("LACTATIONAL")) {
    return contraceptionCategories[8];
  }

  return null;
}

export function normalizeFpSource(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q25_fp_method_source", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("GOVERNMENT") && normalized.includes("HOSPITAL")) {
    return fpSourceCategories[0];
  }

  if (normalized.includes("RHU") || normalized.includes("HEALTH CENTER")) {
    return fpSourceCategories[1];
  }

  if (normalized.includes("BARANGAY HEALTH") || normalized.includes("BRGY")) {
    return fpSourceCategories[2];
  }

  if (normalized.includes("PRIVATE") && normalized.includes("HOSPITAL")) {
    return fpSourceCategories[3];
  }

  if (normalized.includes("PHARMACY")) {
    return fpSourceCategories[4];
  }

  return null;
}

export function normalizeYesNo(value: unknown): "Yes" | "No" | null {
  const normalized = normalizeStandardKey(value);

  if (["1", "YES", "Y", "WITH INTENTION", "REGISTERED"].includes(normalized) || normalized.startsWith("YES ")) {
    return "Yes";
  }

  if (["0", "2", "NO", "N", "NONE", "WITHOUT INTENTION", "UNREGISTERED"].includes(normalized) || normalized.startsWith("NO ")) {
    return "No";
  }

  return null;
}

export function normalizeFpMethod(value: unknown): Category | null {
  return normalizeContraceptionMethod(value);
}

export function normalizePreferredFpMethod(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q26_intended_fp_method", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("TRADITIONAL")) {
    return preferredFpMethodCategories.find((category) => category.key === "traditional") ?? null;
  }

  if (normalized.includes("OTHER")) {
    return preferredFpMethodCategories.find((category) => category.key === "others") ?? null;
  }

  return normalizeContraceptionMethod(cleaned || value);
}

export function normalizeFpIntention(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q26_intention_to_use_fp", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("YES") || normalized.includes("WITH INTENTION")) {
    return fpIntentionCategories[0];
  }

  if (normalized.includes("NO") || normalized.includes("WITHOUT INTENTION")) {
    return fpIntentionCategories[1];
  }

  return null;
}

export function normalizeHealthInsurance(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q27_health_insurance", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("PAYING") && normalized.includes("DEPENDENT")) {
    return healthInsuranceCategories[1];
  }

  if (normalized.includes("PAYING")) {
    return healthInsuranceCategories[0];
  }

  if (normalized.includes("INDIGENT") && normalized.includes("DEPENDENT")) {
    return healthInsuranceCategories[3];
  }

  if (normalized.includes("INDIGENT")) {
    return healthInsuranceCategories[2];
  }

  if (normalized.includes("GSIS")) {
    return healthInsuranceCategories[4];
  }

  if (normalized.includes("SSS")) {
    return healthInsuranceCategories[5];
  }

  if (normalized.includes("PRIVATE") || normalized.includes("HMO")) {
    return healthInsuranceCategories[6];
  }

  if (normalized.includes("OTHER")) {
    return healthInsuranceCategories[7];
  }

  return null;
}

export function normalizeHealthFacilityReason(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q29_health_facility_visit_reason", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("SICK") || normalized.includes("INJURED")) {
    return healthFacilityReasonCategories[0];
  }

  if (normalized.includes("PRENATAL") || normalized.includes("POSTNATAL")) {
    return healthFacilityReasonCategories[1];
  }

  if (normalized.includes("GAVE BIRTH")) {
    return healthFacilityReasonCategories[2];
  }

  if (normalized.includes("DENTAL")) {
    return healthFacilityReasonCategories[3];
  }

  if (normalized.includes("CHECK")) {
    return healthFacilityReasonCategories[4];
  }

  if (normalized.includes("REQUIREMENT") && !(normalized.includes("NHTS") || normalized.includes("CCT") || normalized.includes("4PS"))) {
    return healthFacilityReasonCategories[5];
  }

  if (normalized.includes("NHTS") || normalized.includes("CCT") || normalized.includes("4PS")) {
    return healthFacilityReasonCategories[6];
  }

  if (normalized.includes("OTHER")) {
    return healthFacilityReasonCategories[7];
  }

  return null;
}

export function normalizeHealthFacilityType(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q28_facility_visited", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("GOVERNMENT") && normalized.includes("HOSPITAL")) {
    return healthFacilityTypeCategories[0];
  }

  if (normalized.includes("RHU") || normalized.includes("HEALTH CENTER")) {
    return healthFacilityTypeCategories[1];
  }

  if (normalized.includes("BARANGAY HEALTH") || normalized.includes("BRGY")) {
    return healthFacilityTypeCategories[2];
  }

  if (normalized.includes("PRIVATE") && normalized.includes("HOSPITAL")) {
    return healthFacilityTypeCategories[3];
  }

  if (normalized.includes("PRIVATE") && normalized.includes("CLINIC")) {
    return healthFacilityTypeCategories[4];
  }

  if (normalized.includes("PHARMACY")) {
    return healthFacilityTypeCategories[5];
  }

  if (normalized.includes("HILOT") || normalized.includes("HERBAL")) {
    return healthFacilityTypeCategories[6];
  }

  if (normalized.includes("OTHER")) {
    return healthFacilityTypeCategories[7];
  }

  return null;
}

export function normalizeDisabilityType(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q30_disability", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("VISUAL") || normalized.includes("HEARING") || normalized.includes("SPEECH") || normalized.includes("SENSORY")) {
    return disabilityTypeCategories[0];
  }

  if (normalized.includes("ORTHOPEDIC") || normalized.includes("PHYSICAL")) {
    return disabilityTypeCategories[1];
  }

  if (normalized.includes("MENTAL") || normalized.includes("PSYCHOSOCIAL") || normalized.includes("LEARNING") || normalized.includes("CHRONIC")) {
    return disabilityTypeCategories[2];
  }

  if (normalized.includes("MULTIPLE")) {
    return disabilityTypeCategories[0];
  }

  return null;
}

export function normalizeParentageStatus(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q31_solo_parent", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (normalized.includes("REGISTERED") && !normalized.includes("UNREGISTERED")) {
    return parentageCategories[0];
  }

  if (normalized.includes("NON SOLO") || normalized.includes("NONSOLO") || normalized.includes("NON SOLO PARENT")) {
    return parentageCategories[1];
  }

  if (normalized.includes("UNREGISTERED")) {
    return parentageCategories[2];
  }

  return null;
}

export function normalizeSeniorCitizenRegistration(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q32_registered_senior_citizen", value);
  const yesNo = normalizeYesNo(cleaned || value);

  if (yesNo === "Yes") {
    return seniorCitizenCategories[0];
  }

  if (yesNo === "No") {
    return seniorCitizenCategories[1];
  }

  return null;
}

export function normalizeVoterRegistration(value: unknown): Category | null {
  const normalized = normalizeStandardKey(value);

  if (!normalized || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("ANOTHER") || normalized.includes("OTHER BARANGAY")) {
    return voterCategories[1];
  }

  if (normalized.includes("UNREGISTERED") || normalized === "NO" || normalized === "0") {
    return voterCategories[2];
  }

  if (normalized.includes("REGISTERED") || normalized === "YES" || normalized === "1") {
    return voterCategories[0];
  }

  return null;
}

export function normalizeSkill(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q45_skills", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  return matchCategory(normalized, skillCategories, {
    refrigerationAirconditioning: ["REFRIGERATION", "AIRCONDITIONING"],
    automotiveHeavyEquipment: ["AUTOMOTIVE", "HEAVY EQUIPMENT SERVICING"],
    metalWorker: ["METAL"],
    buildingWiring: ["WIRING"],
    heavyEquipment: ["HEAVY EQUIPMENT OPERATION"],
    plumbing: ["PLUMBING"],
    welding: ["WELDING"],
    carpentry: ["CARPENTRY"],
    baking: ["BAKING"],
    dressmaking: ["DRESSMAKING"],
    linguist: ["LINGUIST"],
    computerGraphics: ["COMPUTER"],
    painting: ["PAINTING"],
    beautyCare: ["BEAUTY"],
    commercialCooking: ["COMMERCIAL COOKING"],
    housekeeping: ["HOUSEKEEPING"],
    massageTherapy: ["MASSAGE"],
    others: ["OTHER"],
  });
}

export function normalizePrimaryNeed(value: unknown): Category | null {
  const normalized = normalizeStandardKey(value);

  if (!normalized || isNotApplicableOrUnknown(normalized) || normalized === "NONE") {
    return null;
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function normalizeTransferYear(value: unknown): Category | null {
  const normalized = normalizeStandardKey(value);
  const numericYear = Number((normalized.match(/\b(19|20)\d{2}\b/) ?? [])[0]);

  if (Number.isFinite(numericYear)) {
    const yearsAgo = Math.max(0, new Date().getFullYear() - numericYear);

    if (yearsAgo <= 0) {
      return transferYearCategories[0];
    }

    if (yearsAgo === 1) {
      return transferYearCategories[1];
    }

    if (yearsAgo === 2) {
      return transferYearCategories[2];
    }

    if (yearsAgo === 3) {
      return transferYearCategories[3];
    }

    return transferYearCategories[4];
  }

  if (normalized.includes("6 MONTH") || normalized.includes("SIX MONTH") || normalized.includes("TRANSIENT")) {
    return transferYearCategories[0];
  }

  if (normalized.includes("1 YEAR") || normalized.includes("ONE YEAR")) {
    return transferYearCategories[1];
  }

  if (normalized.includes("2 YEAR") || normalized.includes("TWO YEAR")) {
    return transferYearCategories[2];
  }

  if (normalized.includes("3 YEAR") || normalized.includes("THREE YEAR")) {
    return transferYearCategories[3];
  }

  if (normalized.includes("4 YEAR") || normalized.includes("FOUR YEAR")) {
    return transferYearCategories[4];
  }

  return null;
}

export function normalizeReasonLeavingPreviousBarangay(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q39_reason_leaving_previous_residence", value);
  const normalized = normalizeStandardKey(cleaned || value);

  return matchCategory(normalized, reasonLeavingCategories, {
    lackEmployment: ["LACK OF EMPLOYMENT"],
    betterIncome: ["BETTER INCOME"],
    schooling: ["SCHOOL"],
    relativesFriends: ["RELATIVES", "FRIENDS"],
    jobRelocation: ["JOB RELOCATION", "EMPLOYMENT"],
    disasterRelocation: ["DISASTER"],
    retirement: ["RETIREMENT"],
    liveWithParents: ["PARENTS"],
    liveWithChildren: ["CHILDREN"],
    marriage: ["MARRIAGE"],
    separation: ["ANNULMENT", "DIVORCE", "SEPARATION"],
    commuting: ["COMMUTING"],
    health: ["HEALTH"],
    peaceSecurity: ["PEACE", "SECURITY"],
    others: ["OTHER"],
  });
}

export function normalizeReasonTransferringCurrentBarangay(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q41_reason_transferring_barangay", value);
  const normalized = normalizeStandardKey(cleaned || value);

  return matchCategory(normalized, reasonTransferringCategories, {
    availabilityJobs: ["AVAILABILITY OF JOB", "JOBS"],
    higherWage: ["HIGHER WAGE", "WAGE"],
    schoolsUniversities: ["SCHOOL", "UNIVERSIT"],
    relativesFriends: ["RELATIVES", "FRIENDS"],
    housing: ["HOUSING", "HOUSE"],
    others: ["OTHER"],
  });
}

export function normalizeIntendedLengthOfStay(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q42_duration_of_stay_current_barangay", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (
    normalized.includes("WILL NOT TRANSFER") ||
    normalized.includes("NO PLAN") ||
    normalized.includes("STAY PERMANENT") ||
    normalized.includes("PERMANENTLY") ||
    normalized === "NO"
  ) {
    return intendedLengthOfStayCategories[6];
  }

  if (normalized.includes("6") || normalized.includes("SIX") || normalized.includes("ABOVE") || normalized.includes("UP")) {
    return intendedLengthOfStayCategories[5];
  }

  if (normalized.includes("5") || normalized.includes("FIVE")) {
    return intendedLengthOfStayCategories[4];
  }

  if (normalized.includes("4") || normalized.includes("FOUR")) {
    return intendedLengthOfStayCategories[3];
  }

  if (normalized.includes("3") || normalized.includes("THREE")) {
    return intendedLengthOfStayCategories[2];
  }

  if (normalized.includes("2") || normalized.includes("TWO")) {
    return intendedLengthOfStayCategories[1];
  }

  if (normalized.includes("1") || normalized.includes("ONE")) {
    return intendedLengthOfStayCategories[0];
  }

  return null;
}

export function normalizeIntendedResidenceFuture(value: unknown, currentBarangay?: unknown): Category | null {
  const normalized = normalizeStandardKey(value);
  const normalizedCurrentBarangay = normalizeStandardKey(currentBarangay);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (
    normalized === normalizedCurrentBarangay ||
    (Boolean(normalizedCurrentBarangay) && normalized.includes(normalizedCurrentBarangay)) ||
    normalized.includes("CURRENT BARANGAY") ||
    normalized.includes("SAME BARANGAY") ||
    normalized.includes("STAY HERE") ||
    normalized.includes("THIS BARANGAY")
  ) {
    return intendedResidenceCategories[0];
  }

  if (
    normalized.includes("OTHER PLACE") ||
    normalized.includes("ANOTHER PLACE") ||
    normalized.includes("OUTSIDE") ||
    normalized.includes("TRANSFER") ||
    normalized !== normalizedCurrentBarangay
  ) {
    return intendedResidenceCategories[1];
  }

  return null;
}

export function normalizeHousingCharacteristics(value: unknown): Category | null {
  const cleaned = cleanCodedValue("q46_housing_unit_tenure", value);
  const normalized = normalizeStandardKey(cleaned || value);

  if (!normalized || normalized === "NONE" || isNotApplicableOrUnknown(normalized)) {
    return null;
  }

  if (normalized.includes("WITHOUT CONSENT")) {
    return housingCharacteristicCategories[0];
  }

  if (normalized.includes("WITH CONSENT")) {
    return housingCharacteristicCategories[1];
  }

  if (normalized.includes("RENTED") || normalized === "RENT") {
    return housingCharacteristicCategories[2];
  }

  if (normalized.includes("OWNED") || normalized.includes("AMORTIZED")) {
    return housingCharacteristicCategories[3];
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function normalizeCauseOfDeath(value: unknown): Category | null {
  const normalized = normalizeStandardKey(value);

  if (!normalized || isNotApplicableOrUnknown(normalized) || normalized === "NONE") {
    return null;
  }

  return { key: toStableKey(normalized), label: normalized };
}

export function getResidentAgeYears(resident: RbimResidentProfile) {
  return resolveResidentBirthdate(resident).ageYears;
}

export function getResidentAgeMonths(resident: RbimResidentProfile) {
  const resolvedBirthdate = resolveResidentBirthdate(resident);

  if (resolvedBirthdate.precision !== "exact") {
    return null;
  }

  const ageReferenceDate = parseMMDDYYYY(resolvedBirthdate.ageAsOfDate);

  if (!ageReferenceDate) {
    return resident.ageMonths;
  }

  const ageDetails = calculateAgeDetailsFromBirthdate(
    resolvedBirthdate.compatibilityDate,
    ageReferenceDate,
  );

  return ageDetails?.months ?? null;
}

export function getResidentSex(resident: RbimResidentProfile) {
  return resident.sex || getResidentValue(resident, ["sex", "qSex", "Q3"], ["Q3"]);
}

export function getResidentMigrationStatus(resident: RbimResidentProfile) {
  return resident.migrationStatus || getResidentValue(resident, ["migrationStatus", "migration_status", "residentType", "qMigrationStatus", "Q37"], ["Q37"]);
}

export function getResidentCivilStatus(resident: RbimResidentProfile) {
  return resident.civilStatus || getResidentValue(resident, ["civilStatus", "maritalStatus", "q08", "Q8"], ["Q8"]);
}

export function getResidentReligion(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["religion", "Q9"], ["Q9"]);
}

export function getResidentEthnicity(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["ethnicity", "Q10"], ["Q10"]);
}

export function getResidentEducationLevel(resident: RbimResidentProfile) {
  return resident.educationLevel || getResidentValue(resident, ["highestEducation", "educationLevel", "Q11"], ["Q11"]);
}

export function getResidentEnrollmentStatus(resident: RbimResidentProfile) {
  return resident.schoolAttendance || getResidentValue(resident, ["currentlyEnrolled", "schoolAttendance", "Q12"], ["Q12"]);
}

export function getResidentSchoolType(resident: RbimResidentProfile) {
  return getResidentEnrollmentStatus(resident);
}

export function getResidentSchoolLevel(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["schoolLevel", "Q13"], ["Q13"]);
}

export function getResidentMonthlyIncome(resident: RbimResidentProfile) {
  return resident.income || getResidentValue(resident, ["monthlyIncome", "income", "Q16"], ["Q16"]);
}

export function getResidentIncomeSource(resident: RbimResidentProfile) {
  return resident.sourceOfIncome || getResidentValue(resident, ["sourceOfIncome", "incomeSource", "Q17"], ["Q17"]);
}

export function getResidentWorkStatus(resident: RbimResidentProfile) {
  return resident.workStatus || getResidentValue(resident, ["workStatus", "statusOfWork", "Q18"], ["Q18"]);
}

export function getResidentPlaceOfDelivery(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["placeOfDelivery", "deliveryPlace", "Q20"], ["Q20"]);
}

export function getResidentBirthAttendant(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["birthAttendant", "Q21"], ["Q21"]);
}

export function getResidentLastImmunization(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["immunization", "lastImmunization", "Q22"], ["Q22"]);
}

export function getResidentGravida(resident: RbimResidentProfile) {
  const value = getResidentValue(resident, ["gravida", "livingChildren", "totalPregnancies", "Q23"], ["Q23"]);

  return splitPregnancyValue(value)[0];
}

export function getResidentLivingChildren(resident: RbimResidentProfile) {
  const direct = getResidentValue(resident, ["livingChildrenAlive", "numberOfLivingChildren"], []);

  if (direct) {
    return direct;
  }

  const value = getResidentValue(resident, ["livingChildren", "Q23"], ["Q23"]);

  return splitPregnancyValue(value)[1] ?? splitPregnancyValue(value)[0];
}

export function getResidentContraceptionUse(resident: RbimResidentProfile) {
  return getResidentContraceptionMethod(resident);
}

export function getResidentFpCurrentUse(resident: RbimResidentProfile) {
  return getResidentContraceptionMethod(resident);
}

export function getResidentContraceptionMethod(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["familyPlanningUse", "contraceptionMethod", "Q24"], ["Q24"]);
}

export function getResidentFpSource(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["fpMethodSource", "familyPlanningSource", "Q25"], ["Q25"]);
}

export function getResidentFpIntention(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["intentionToUseFp", "fpIntention", "Q26"], ["Q26"]);
}

export function getResidentPreferredFpMethod(resident: RbimResidentProfile) {
  const directDetail = getResidentValue(resident, ["intentionToUseFpDetail", "preferredFpMethod", "intendedFpMethod"], []);

  if (hasUsableValue(directDetail)) {
    return directDetail;
  }

  const intention = String(getResidentFpIntention(resident) ?? "");
  const parts = intention.split(",").map((part) => part.trim()).filter(Boolean);

  return parts.length > 1 ? parts.slice(1).join(", ") : undefined;
}

export function getResidentHealthInsurance(resident: RbimResidentProfile) {
  return resident.healthInsurance || getResidentValue(resident, ["healthInsurance", "Q27"], ["Q27"]);
}

export function getResidentVisitedHealthFacility(resident: RbimResidentProfile) {
  return getResidentHealthFacilityVisited(resident);
}

export function getResidentHealthFacilityReason(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["healthFacilityVisitReason", "facilityVisitReason", "Q29"], ["Q29"]);
}

export function getResidentHealthFacilityVisited(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["facilityVisitedPast12Months", "healthFacilityVisited", "facilityVisited", "Q28"], ["Q28"]);
}

export function getResidentDisabilityStatus(resident: RbimResidentProfile) {
  return resident.disability || getResidentValue(resident, ["disability", "Q30"], ["Q30"]);
}

export function getResidentDisabilityType(resident: RbimResidentProfile) {
  return getResidentDisabilityStatus(resident);
}

export function getResidentParentageStatus(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["soloParentStatus", "parentageStatus", "Q31"], ["Q31"]);
}

export function getResidentSeniorCitizenStatus(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["registeredSeniorCitizen", "seniorCitizenStatus", "Q32"], ["Q32"]);
}

export function getResidentVoterRegistrationStatus(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["registeredVoterBarangay", "voterRegistrationStatus", "Q33"], ["Q33"]);
}

export function getResidentSkills(resident: RbimResidentProfile): unknown[] {
  const value = getResidentValue(resident, ["skills", "Q45"], ["Q45"]);

  return splitMultiValue(value);
}

export function getResidentTransferYear(resident: RbimResidentProfile) {
  return getResidentValue(resident, ["dateOfTransfer", "transferYear", "Q38"], ["Q38"]);
}

export function getResidentReasonLeavingPreviousBarangay(resident: RbimResidentProfile): unknown[] {
  return [
    getResidentValue(resident, ["reasonLeavingPreviousResidence1", "Q39A"], ["Q39A"]),
    getResidentValue(resident, ["reasonLeavingPreviousResidence2", "Q39B"], ["Q39B"]),
    getResidentValue(resident, ["reasonLeavingPreviousResidence3", "Q39C"], ["Q39C"]),
  ].flatMap(splitMultiValue);
}

export function getResidentReasonTransferringCurrentBarangay(resident: RbimResidentProfile): unknown[] {
  return [
    getResidentValue(resident, ["transferReason1", "Q41A"], ["Q41A"]),
    getResidentValue(resident, ["transferReason2", "Q41B"], ["Q41B"]),
    getResidentValue(resident, ["transferReason3", "Q41C"], ["Q41C"]),
  ].flatMap(splitMultiValue);
}

export function getResidentIntendedLengthOfStay(resident: RbimResidentProfile) {
  return getResidentValue(
    resident,
    ["durationOfStayCurrentBarangay", "intendedLengthOfStay", "intendedStay", "Q42"],
    ["Q42"],
  );
}

export function getResidentBarangay(resident: RbimResidentProfile) {
  return resident.barangay || getResidentValue(resident, ["barangay", "household.barangay"], []);
}

export function getResidentCityMunicipality(resident: RbimResidentProfile) {
  return resident.cityMunicipality || getResidentValue(resident, ["cityMunicipality", "municipality", "household.municipality"], []);
}

export function getHouseholdMembers(household: RbimHouseholdProfile) {
  return Array.isArray(household.members) ? household.members : [];
}

export function getHouseholdId(household: RbimHouseholdProfile) {
  return household.householdId || household.householdCode || household.householdNumber || household.submissionId;
}

export function getHouseholdBarangay(household: RbimHouseholdProfile) {
  return household.barangay || getNestedValue(household.cleanedData, ["household.barangay", "barangay"]);
}

export function getHouseholdHead(household: RbimHouseholdProfile) {
  return (
    getHouseholdMembers(household).find((member) => /head/i.test(member.relationshipToHouseholdHead)) ??
    getHouseholdMembers(household).find((member) => normalizeComparable(member.fullName) === normalizeComparable(household.householdHeadName)) ??
    getHouseholdMembers(household)[0] ??
    null
  );
}

export function getHouseholdSize(household: RbimHouseholdProfile) {
  const directSize = Number(household.totalMembers);

  if (Number.isFinite(directSize) && directSize > 0) {
    return directSize;
  }

  return getHouseholdMembers(household).length;
}

export function getHouseholdHeadSex(household: RbimHouseholdProfile) {
  return getHouseholdHead(household)?.sex ?? getNestedValue(household.cleanedData, ["householdHeadSex", "headSex"]);
}

export function getHouseholdHeadMigrationStatus(household: RbimHouseholdProfile) {
  return getHouseholdHead(household)?.migrationStatus ?? getNestedValue(household.cleanedData, ["migrationStatus", "residentType"]);
}

export function getHouseholdPrimaryNeeds(household: RbimHouseholdProfile): unknown[] {
  return [
    getNestedValue(household.cleanedData, ["householdQuestions.primaryNeed1", "primaryNeed1"]),
    getNestedValue(household.cleanedData, ["householdQuestions.primaryNeed2", "primaryNeed2"]),
    getNestedValue(household.cleanedData, ["householdQuestions.primaryNeed3", "primaryNeed3"]),
    getNestedValue(household.rawSource, ["householdQuestions.primaryNeed1", "primaryNeed1"]),
    getNestedValue(household.rawSource, ["householdQuestions.primaryNeed2", "primaryNeed2"]),
    getNestedValue(household.rawSource, ["householdQuestions.primaryNeed3", "primaryNeed3"]),
  ].flatMap(splitMultiValue);
}

export function getHouseholdIntendedResidenceFuture(household: RbimHouseholdProfile) {
  const futureBarangay =
    getNestedValue(household.cleanedData, ["householdQuestions.futureBarangay", "futureBarangay"]) ??
    getNestedValue(household.rawSource, ["householdQuestions.futureBarangay", "futureBarangay"]);
  const futureMunicipality =
    getNestedValue(household.cleanedData, ["householdQuestions.futureMunicipality", "futureMunicipality"]) ??
    getNestedValue(household.rawSource, ["householdQuestions.futureMunicipality", "futureMunicipality"]);
  const futureProvince =
    getNestedValue(household.cleanedData, ["householdQuestions.futureProvince", "futureProvince"]) ??
    getNestedValue(household.rawSource, ["householdQuestions.futureProvince", "futureProvince"]);

  return [futureBarangay, futureMunicipality, futureProvince].filter(hasUsableValue).join(" / ");
}

export function getHouseholdHousingCharacteristics(household: RbimHouseholdProfile) {
  return (
    getNestedValue(household.cleanedData, ["householdQuestions.housingTenure", "housingTenure", "Q46"]) ??
    getNestedValue(household.rawSource, ["householdQuestions.housingTenure", "housingTenure", "Q46"])
  );
}

function getHouseholdCommonDiseaseCauses(household: RbimHouseholdProfile): unknown[] {
  return [
    getNestedValue(household.cleanedData, ["householdQuestions.commonDiseaseCause1", "commonDiseaseCause1"]),
    getNestedValue(household.cleanedData, ["householdQuestions.commonDiseaseCause2", "commonDiseaseCause2"]),
    getNestedValue(household.cleanedData, ["householdQuestions.commonDiseaseCause3", "commonDiseaseCause3"]),
    getNestedValue(household.rawSource, ["householdQuestions.commonDiseaseCause1", "commonDiseaseCause1"]),
    getNestedValue(household.rawSource, ["householdQuestions.commonDiseaseCause2", "commonDiseaseCause2"]),
    getNestedValue(household.rawSource, ["householdQuestions.commonDiseaseCause3", "commonDiseaseCause3"]),
  ].flatMap(splitMultiValue);
}

export type ReportedDeath = {
  sex?: unknown;
  age?: unknown;
  cause?: unknown;
};

export function getReportedDeaths(households: RbimHouseholdProfile[]): ReportedDeath[] {
  return households.flatMap((household) => {
    const deaths: ReportedDeath[] = [];
    const femaleDeathFlag = getNestedValue(household.cleanedData, ["householdQuestions.femaleDeathPast12Months", "femaleDeathPast12Months"]);
    const childDeathFlag = getNestedValue(household.cleanedData, ["householdQuestions.childDeathPast12Months", "childDeathPast12Months"]);
    const femaleDeathAge = getNestedValue(household.cleanedData, ["householdQuestions.femaleDeathAge", "femaleDeathAge"]) ??
      getNestedValue(household.rawSource, ["householdQuestions.femaleDeathAge", "femaleDeathAge"]);
    const femaleDeathCause = getNestedValue(household.cleanedData, ["householdQuestions.femaleDeathCause", "femaleDeathCause"]) ??
      getNestedValue(household.rawSource, ["householdQuestions.femaleDeathCause", "femaleDeathCause"]);
    const childDeathAge = getNestedValue(household.cleanedData, ["householdQuestions.childDeathAge", "childDeathAge"]) ??
      getNestedValue(household.rawSource, ["householdQuestions.childDeathAge", "childDeathAge"]);
    const childDeathSex = getNestedValue(household.cleanedData, ["householdQuestions.childDeathSex", "childDeathSex"]) ??
      getNestedValue(household.rawSource, ["householdQuestions.childDeathSex", "childDeathSex"]);
    const childDeathCause = getNestedValue(household.cleanedData, ["householdQuestions.childDeathCause", "childDeathCause"]) ??
      getNestedValue(household.rawSource, ["householdQuestions.childDeathCause", "childDeathCause"]);

    if (isAffirmativeDeathFlag(femaleDeathFlag) || hasUsableValue(femaleDeathAge) || hasUsableValue(femaleDeathCause)) {
      deaths.push({
        sex: "Female",
        age: femaleDeathAge,
        cause: femaleDeathCause,
      });
    }

    if (isAffirmativeDeathFlag(childDeathFlag) || hasUsableValue(childDeathAge) || hasUsableValue(childDeathCause)) {
      deaths.push({
        sex: childDeathSex,
        age: childDeathAge,
        cause: childDeathCause,
      });
    }

    return deaths;
  });
}

export function getDeathSex(death: ReportedDeath) {
  return death.sex;
}

export function getDeathAge(death: ReportedDeath) {
  const numericValue = Number(String(death.age ?? "").replace(/[^\d.-]/g, ""));

  return Number.isFinite(numericValue) ? numericValue : null;
}

export function getDeathCause(death: ReportedDeath) {
  return death.cause;
}

function generateDynamicSexMigrationReport({
  reportId,
  labelKey,
  label,
  residents,
  context,
  getValue,
  normalizeValue,
  preferredOrder,
}: {
  reportId: number;
  labelKey: string;
  label: string;
  residents: RbimResidentProfile[];
  context: ReportContext;
  getValue: (resident: RbimResidentProfile) => unknown;
  normalizeValue: (value: unknown) => Category;
  preferredOrder: string[];
}) {
  const categories = getOrderedCategories(residents.map((resident) => normalizeValue(getValue(resident))), preferredOrder);
  const rows = (categories.length ? categories : [{ key: "unknown", label: "UNKNOWN" }]).map((category) =>
    createCountRow(labelKey, category.label, createSexMigrationColumns(labelKey, label, true)),
  );
  const columns = createSexMigrationColumns(labelKey, label, true);
  const totals = createCountRow(labelKey, "Total", columns, true);
  const rowByLabel = mapRowsByLabel(rows, labelKey);

  residents.forEach((resident) => {
    const category = normalizeValue(getValue(resident));
    const row = rowByLabel.get(category.label);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const columnKey = getSexMigrationColumnKey(migrationStatus, sex);

    if (!row) {
      return;
    }

    if (!columnKey) {
      increment(row, "invalidResponses");
      increment(totals, "invalidResponses");
    } else {
      increment(row, columnKey);
      increment(totals, columnKey);
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(reportId, context, columns, [...rows, totals]);
}

function generateMaleAgeMigrationCategoryReport({
  reportId,
  rows: rowLabels,
  getAgeGroup,
  categories,
  residents,
  context,
  getValue,
  normalizeValue,
  includeResident,
}: {
  reportId: number;
  rows: readonly string[];
  getAgeGroup: (ageYears: number | null) => string | null;
  categories: Category[];
  residents: RbimResidentProfile[];
  context: ReportContext;
  getValue: (resident: RbimResidentProfile) => unknown;
  normalizeValue: (value: unknown) => Category | null;
  includeResident: (resident: RbimResidentProfile) => boolean;
}) {
  const columns = createMaleMigrationCategoryColumns("ageGroup", "Age", categories);
  const rows = rowLabels.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const category = normalizeValue(getValue(resident));

    if (!includeResident(resident) || sex !== "Male" || migrationStatus === "Invalid" || !ageGroup || !category) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);
    const migrationKey = getMigrationKey(migrationStatus);

    if (!row || !migrationKey) {
      return;
    }

    increment(row, `male_${migrationKey}_${category.key}`);
    increment(row, `male_total_${category.key}`);
    increment(totals, `male_${migrationKey}_${category.key}`);
    increment(totals, `male_total_${category.key}`);
  });

  return buildReportTable(reportId, context, columns, [...rows, totals]);
}

function generateInfantSexReport({
  reportId,
  labelKey,
  label,
  rows: categories,
  residents,
  context,
  getValue,
  normalizeValue,
}: {
  reportId: number;
  labelKey: string;
  label: string;
  rows: Category[];
  residents: RbimResidentProfile[];
  context: ReportContext;
  getValue: (resident: RbimResidentProfile) => unknown;
  normalizeValue: (value: unknown) => Category | null;
}) {
  const columns = [
    { key: labelKey, label },
    { key: "male", label: "Male" },
    { key: "female", label: "Female" },
    { key: "total", label: "Total" },
  ];
  const reportRows = categories.map((category) => createCountRow(labelKey, category.label, columns));
  const totals = createCountRow(labelKey, "Total", columns, true);
  const rowByLabel = mapRowsByLabel(reportRows, labelKey);

  residents.filter(isInfantUnderOneYear).forEach((resident) => {
    const sex = normalizeSex(getResidentSex(resident));
    const category = normalizeValue(getValue(resident));

    if (sex === "Invalid" || !category) {
      return;
    }

    const row = rowByLabel.get(category.label);
    const sexKey = getSexKey(sex);

    if (!row || !sexKey) {
      return;
    }

    increment(row, sexKey);
    increment(row, "total");
    increment(totals, sexKey);
    increment(totals, "total");
  });

  return buildReportTable(reportId, context, columns, [...reportRows, totals]);
}

function generateFemaleAgeMigrationNumberReport({
  reportId,
  residents,
  context,
  getValue,
}: {
  reportId: number;
  residents: RbimResidentProfile[];
  context: ReportContext;
  getValue: (resident: RbimResidentProfile) => unknown;
}) {
  const columns = createMigrationCategoryColumns("ageGroup", "Age", numberBuckets, false, true);
  const rows = ageGroups10To54.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeGroup10To54);
    const sex = normalizeSex(getResidentSex(resident));
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const numberBucket = normalizeNumberBucket(getValue(resident));

    if (sex !== "Female" || migrationStatus === "Invalid" || !ageGroup || !numberBucket) {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);
    const migrationKey = getMigrationKey(migrationStatus);

    if (!row || !migrationKey) {
      return;
    }

    increment(row, `${migrationKey}_${numberBucket.key}`);
    increment(row, `total_${numberBucket.key}`);
    increment(totals, `${migrationKey}_${numberBucket.key}`);
    increment(totals, `total_${numberBucket.key}`);
  });

  return buildReportTable(reportId, context, columns, [...rows, totals]);
}

function generateCauseCountReport({
  reportId,
  labelKey,
  label,
  deaths,
  context,
  bySex,
}: {
  reportId: number;
  labelKey: string;
  label: string;
  deaths: ReportedDeath[];
  context: ReportContext;
  bySex: boolean;
}) {
  const columns = bySex
    ? [
        { key: labelKey, label },
        { key: "male", label: "Male" },
        { key: "female", label: "Female" },
      ]
    : [
        { key: labelKey, label },
        { key: "total", label: "Number of Female Mortality" },
      ];
  const causes = getOrderedCategories(
    deaths.map((death) => normalizeCauseOfDeath(getDeathCause(death))).filter((cause): cause is Category => Boolean(cause)),
    [],
  );
  const rows = (causes.length ? causes : [{ key: "none", label: "NONE" }]).map((cause) =>
    createCountRow(labelKey, cause.label, columns),
  );
  const totals = createCountRow(labelKey, "Total", columns, true);
  const rowByCause = mapRowsByLabel(rows, labelKey);

  deaths.forEach((death) => {
    const cause = normalizeCauseOfDeath(getDeathCause(death));
    const row = cause ? rowByCause.get(cause.label) : null;

    if (!row) {
      return;
    }

    if (bySex) {
      const sexKey = getSexKey(normalizeSex(getDeathSex(death)));

      if (!sexKey) {
        return;
      }

      increment(row, sexKey);
      increment(totals, sexKey);
      return;
    }

    increment(row, "total");
    increment(totals, "total");
  });

  return buildReportTable(reportId, context, columns, [...rows, totals]);
}

function generateMaleMigrantReasonReport({
  reportId,
  categories,
  residents,
  context,
  getValues,
  normalizeValue,
}: {
  reportId: number;
  categories: Category[];
  residents: RbimResidentProfile[];
  context: ReportContext;
  getValues: (resident: RbimResidentProfile) => unknown[];
  normalizeValue: (value: unknown) => Category | null;
}) {
  const columns = [
    { key: "ageGroup", label: "Age" },
    {
      key: "male",
      label: "Male",
      children: [
        {
          key: "male_migrant",
          label: "Migrants",
          children: categories.map((category) => ({
            key: `male_migrant_${category.key}`,
            label: category.label,
          })),
        },
        {
          key: "male_transient",
          label: "Transients",
          children: categories.map((category) => ({
            key: `male_transient_${category.key}`,
            label: category.label,
          })),
        },
      ],
    },
  ];
  const rows = ageGroups0To80.map((ageGroup) => createCountRow("ageGroup", ageGroup, columns));
  const totals = createCountRow("ageGroup", "Total", columns, true);
  const rowByAgeGroup = mapRowsByLabel(rows, "ageGroup");

  residents.forEach((resident) => {
    const ageGroup = getResidentAgeGroup(resident, getAgeBracket);
    const migrationStatus = normalizeMigrationStatus(getResidentMigrationStatus(resident));
    const migrationKey = getMigrationKey(migrationStatus);

    if (normalizeSex(getResidentSex(resident)) !== "Male" || !ageGroup || !migrationKey || migrationStatus === "Non-Migrant") {
      return;
    }

    const row = rowByAgeGroup.get(ageGroup);

    if (!row) {
      return;
    }

    getValues(resident)
      .map(normalizeValue)
      .filter((category): category is Category => Boolean(category))
      .forEach((category) => {
        increment(row, `male_${migrationKey}_${category.key}`);
        increment(totals, `male_${migrationKey}_${category.key}`);
      });
  });

  return buildReportTable(reportId, context, columns, [...rows, totals]);
}

function createSexMigrationColumns(labelKey: string, label: string, includeInvalid: boolean): ReportColumn[] {
  const columns: ReportColumn[] = [
    { key: labelKey, label },
    ...createMigrationSexGroups(),
  ];

  if (includeInvalid) {
    columns.push({ key: "invalidResponses", label: "Invalid Responses" });
  }

  columns.push({ key: "total", label: "Total" });

  return columns;
}

function createMigrationSexGroups(): ReportColumn[] {
  return migrationGroups.map((migration) => ({
    key: migration.key,
    label: migration.label,
    children: [
      { key: `${migration.key}Male`, label: "Male" },
      { key: `${migration.key}Female`, label: "Female" },
    ],
  }));
}

function createMigrationCategoryColumns(
  labelKey: string,
  label: string,
  categories: Category[],
  includeInvalid: boolean,
  includeCategoryTotals = false,
): ReportColumn[] {
  const columns: ReportColumn[] = [
    { key: labelKey, label },
    ...migrationGroups.map((migration) => ({
      key: migration.key,
      label: migration.label,
      children: categories.map((category) => ({
        key: `${migration.key}_${category.key}`,
        label: category.label,
      })),
    })),
  ];

  if (includeInvalid) {
    columns.push({ key: "invalidResponses", label: "Invalid Responses" });
  }

  if (includeCategoryTotals) {
    columns.push({
      key: "totalGroup",
      label: "Total",
      children: categories.map((category) => ({
        key: `total_${category.key}`,
        label: category.label,
      })),
    });
  } else {
    columns.push({ key: "total", label: "Total" });
  }

  return columns;
}

function createNonMigrantSexCategoryColumns(
  labelKey: string,
  label: string,
  categories: Category[],
  includeSubTotal: boolean,
  includeTotalColumn = true,
): ReportColumn[] {
  const children: ReportColumn[] = categories.map((category) => ({
    key: `nonMigrant_${category.key}`,
    label: category.label,
    children: [
      { key: `nonMigrant_${category.key}_male`, label: "M" },
      { key: `nonMigrant_${category.key}_female`, label: "F" },
    ],
  }));

  if (includeSubTotal) {
    children.push({
      key: "nonMigrantSubTotal",
      label: "Sub Total",
      children: [
        { key: "nonMigrantSubTotal_male", label: "M" },
        { key: "nonMigrantSubTotal_female", label: "F" },
      ],
    });
  }

  const columns: ReportColumn[] = [
    { key: labelKey, label },
    {
      key: "nonMigrants",
      label: "Non-Migrants",
      children,
    },
  ];

  if (includeTotalColumn) {
    columns.push({ key: "total", label: "Total" });
  }

  return columns;
}

function createMaleMigrationCategoryColumns(labelKey: string, label: string, categories: Category[]): ReportColumn[] {
  return [
    { key: labelKey, label },
    {
      key: "male",
      label: "Male",
      children: [
        ...migrationGroups.map((migration) => ({
          key: `male_${migration.key}`,
          label: migration.label,
          children: categories.map((category) => ({
            key: `male_${migration.key}_${category.key}`,
            label: category.label,
          })),
        })),
        {
          key: "male_total",
          label: "Total",
          children: categories.map((category) => ({
            key: `male_total_${category.key}`,
            label: category.label,
          })),
        },
      ],
    },
  ];
}

function createNonMigrantCategoryColumns(labelKey: string, label: string, categories: Category[]): ReportColumn[] {
  return [
    { key: labelKey, label },
    {
      key: "nonMigrants",
      label: "Non-Migrants",
      children: categories.map((category) => ({
        key: `nonMigrant_${category.key}`,
        label: category.label,
      })),
    },
  ];
}

function createMigrationTotalColumns(labelKey: string, label: string): ReportColumn[] {
  return [
    { key: labelKey, label },
    { key: "nonMigrant", label: "Non-Migrants" },
    { key: "migrant", label: "Migrants" },
    { key: "transient", label: "Transients" },
    { key: "total", label: "Total" },
  ];
}

function createMigrationSexTotalColumns(labelKey: string, label: string): ReportColumn[] {
  return [
    { key: labelKey, label },
    ...createMigrationSexGroups(),
    {
      key: "totalGroup",
      label: "Total",
      children: [
        { key: "totalMale", label: "Male" },
        { key: "totalFemale", label: "Female" },
      ],
    },
  ];
}

function buildReportTable(
  reportId: number,
  context: ReportContext,
  columns: ReportColumn[],
  rows: ReportRow[],
  isImplemented = true,
  notes?: string[],
): ReportTableData {
  const definition = getReportDefinition(reportId);

  return {
    reportId,
    title: definition.title,
    scopeLabel: context.scopeLabel,
    generatedAt: new Date().toISOString(),
    residentCount: context.residentCount,
    householdCount: context.householdCount,
    isImplemented,
    columns,
    rows,
    notes,
  };
}

function generatePlaceholderReport(definition: ReportDefinition, context: ReportContext): ReportTableData {
  return {
    reportId: definition.id,
    title: definition.title,
    scopeLabel: context.scopeLabel,
    generatedAt: new Date().toISOString(),
    residentCount: context.residentCount,
    householdCount: context.householdCount,
    isImplemented: false,
    columns: [{ key: "message", label: "Report Status" }],
    rows: [
      {
        message:
          "This report layout is ready. Calculation logic will be connected once the required RBIM field mapping is finalized.",
      },
    ],
  };
}

function createCountRow(labelKey: string, label: string | number, columns: ReportColumn[], isTotal = false): ReportRow {
  const row: ReportRow = {
    [labelKey]: String(label),
    isTotal,
  };

  flattenReportColumns(columns)
    .filter((column) => column.key !== labelKey)
    .forEach((column) => {
      row[column.key] = 0;
    });

  return row;
}

function increment(row: ReportRow, key: string, amount = 1) {
  row[key] = Number(row[key] ?? 0) + amount;
}

function mapRowsByLabel(rows: ReportRow[], labelKey: string) {
  return new Map(rows.map((row) => [String(row[labelKey]), row]));
}

function getSexMigrationColumnKey(migrationStatus: MigrationStatus, sex: Sex) {
  const migrationKey = getMigrationKey(migrationStatus);
  const sexKey = getSexKey(sex);

  return migrationKey && sexKey ? `${migrationKey}${capitalize(sexKey)}` : null;
}

function getMigrationKey(status: MigrationStatus) {
  return migrationGroups.find((group) => group.value === status)?.key ?? null;
}

function getSexKey(sex: Sex) {
  if (sex === "Male") {
    return "male";
  }

  if (sex === "Female") {
    return "female";
  }

  return null;
}

function getReportDefinition(reportId: number) {
  return REPORT_DEFINITIONS.find((report) => report.id === reportId) ?? REPORT_DEFINITIONS[0];
}

function getScopeLabel(
  options: ReportScopeOptions,
  residents: RbimResidentProfile[],
  households: RbimHouseholdProfile[],
) {
  if (options.municipalityWide) {
    return options.municipality || residents[0]?.cityMunicipality || households[0]?.cityMunicipality || "Municipality-wide";
  }

  return options.barangay || residents[0]?.barangay || households[0]?.barangay || "Selected Barangay";
}

function getHouseholdSizeRows(households: RbimHouseholdProfile[]) {
  const sizes = Array.from(new Set(households.map(getHouseholdSize).filter((size) => Number.isFinite(size) && size > 0)))
    .sort((a, b) => a - b)
    .map(String);

  return sizes.length ? sizes : ["1"];
}

function getOrderedCategories(categories: Category[], preferredOrder: string[]) {
  const byLabel = new Map<string, Category>();

  categories.forEach((category) => {
    if (category.label) {
      byLabel.set(category.label, category);
    }
  });

  const ordered: Category[] = [];

  preferredOrder.forEach((label) => {
    const category = byLabel.get(label);

    if (category) {
      ordered.push(category);
      byLabel.delete(label);
    }
  });

  return [
    ...ordered,
    ...Array.from(byLabel.values()).sort((a, b) => a.label.localeCompare(b.label)),
  ];
}

function getAgeGroup3To24(ageYears: number | null) {
  if (!isValidAge(ageYears) || ageYears < 3 || ageYears > 24) {
    return null;
  }

  if (ageYears <= 4) {
    return "3-4";
  }

  return getAgeBracket(ageYears);
}

function getAgeGroup15AndOver(ageYears: number | null) {
  if (!isValidAge(ageYears) || ageYears < 15) {
    return null;
  }

  return getAgeBracket(ageYears);
}

function getAgeGroup10To54(ageYears: number | null) {
  if (!isValidAge(ageYears) || ageYears < 10 || ageYears > 54) {
    return null;
  }

  return getAgeBracket(ageYears);
}

function getEducationAgeGroup(ageYears: number | null) {
  if (!isValidAge(ageYears) || ageYears < 5) {
    return null;
  }

  if (ageYears <= 9) {
    return "6-9";
  }

  if (ageYears >= 65) {
    return "65+";
  }

  return getAgeBracket(ageYears);
}

export function getResidentAgeGroup(
  resident: RbimResidentProfile,
  getAgeGroup: (ageYears: number | null) => string | null,
) {
  const resolvedBirthdate = resolveResidentBirthdate(resident);
  const minimumAge = resolvedBirthdate.minimumAgeYears;
  const maximumAge = resolvedBirthdate.maximumAgeYears;

  if (!isValidAge(minimumAge) || !isValidAge(maximumAge)) {
    return null;
  }

  const minimumGroup = getAgeGroup(minimumAge);
  const maximumGroup = getAgeGroup(maximumAge);

  return minimumGroup && minimumGroup === maximumGroup
    ? minimumGroup
    : null;
}

export function isResidentAgeInRange(
  resident: RbimResidentProfile,
  min: number,
  max: number,
) {
  const resolvedBirthdate = resolveResidentBirthdate(resident);
  const minimumAge = resolvedBirthdate.minimumAgeYears;
  const maximumAge = resolvedBirthdate.maximumAgeYears;

  return (
    isValidAge(minimumAge) &&
    isValidAge(maximumAge) &&
    minimumAge >= min &&
    maximumAge <= max
  );
}

function isFemaleNonMigrantInAgeRange(resident: RbimResidentProfile, min: number, max: number) {
  return (
    normalizeSex(getResidentSex(resident)) === "Female" &&
    normalizeMigrationStatus(getResidentMigrationStatus(resident)) === "Non-Migrant" &&
    isResidentAgeInRange(resident, min, max)
  );
}

function isInfantUnderOneYear(resident: RbimResidentProfile) {
  return isResidentAgeInRange(resident, 0, 0);
}

function isModernFpMethod(category: Category | null) {
  return Boolean(category && category.key !== "none");
}

function normalizeNumberBucket(value: unknown): Category | null {
  const rawValue = String(value ?? "").split("/")[0].replace(/[^\d-]/g, "").trim();
  const numericValue = Number(rawValue);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  if (numericValue >= 10) {
    return numberBuckets[9];
  }

  return numberBuckets[numericValue - 1] ?? null;
}

function splitPregnancyValue(value: unknown): [string, string?] {
  const parts = String(value ?? "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);

  return [parts[0] ?? "", parts[1]];
}

function getResidentValue(
  resident: RbimResidentProfile,
  fieldPaths: string[],
  questionNumbers: string[],
) {
  const residentRecord = resident as unknown as Record<string, unknown>;
  const sources = [residentRecord, resident.cleanedData, resident.rawSource];

  for (const path of fieldPaths) {
    for (const source of sources) {
      const value = getNestedValue(source, [path]);

      if (hasUsableValue(value)) {
        return value;
      }
    }
  }

  const answerValue = getAnswerValue(resident.cleanedData, fieldPaths, questionNumbers);

  if (hasUsableValue(answerValue)) {
    return answerValue;
  }

  return undefined;
}

function getAnswerValue(source: Record<string, unknown> | undefined, fieldPaths: string[], questionNumbers: string[]) {
  const answers = getNestedValue(source, ["memberAnswers"]);

  if (!Array.isArray(answers)) {
    return undefined;
  }

  const normalizedFields = fieldPaths.map((field) => {
    const parts = field.split(".");

    return normalizeComparable(parts[parts.length - 1]);
  });
  const normalizedQuestions = questionNumbers.map(normalizeComparable);
  const answer = answers.find((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const record = item as Record<string, unknown>;
    const field = normalizeComparable(record.field);
    const questionNumber = normalizeComparable(record.question_number);

    return normalizedFields.includes(field) || normalizedQuestions.includes(questionNumber);
  }) as Record<string, unknown> | undefined;

  return answer?.cleaned_value ?? answer?.raw_value;
}

function getNestedValue(source: Record<string, unknown> | undefined, keys: string[]) {
  if (!source) {
    return undefined;
  }

  for (const key of keys) {
    const parts = key.split(".");
    let currentValue: unknown = source;

    for (const part of parts) {
      if (!currentValue || typeof currentValue !== "object") {
        currentValue = undefined;
        break;
      }

      currentValue = (currentValue as Record<string, unknown>)[part];
    }

    if (hasUsableValue(currentValue)) {
      return currentValue;
    }
  }

  return undefined;
}

function hasUsableValue(value: unknown) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function splitMultiValue(value: unknown): unknown[] {
  const text = String(value ?? "").trim();

  if (!text || isNotApplicableOrUnknown(normalizeStandardKey(text))) {
    return [];
  }

  return text
    .split(/[,;]+/g)
    .map((part) => part.trim())
    .filter(Boolean);
}

function matchCategory(
  normalizedValue: string,
  categories: Category[],
  keywordMap: Record<string, string[]>,
): Category | null {
  if (!normalizedValue || normalizedValue === "NONE" || isNotApplicableOrUnknown(normalizedValue)) {
    return null;
  }

  for (const category of categories) {
    const keywords = keywordMap[category.key] ?? [category.label];

    if (keywords.some((keyword) => normalizedValue.includes(normalizeStandardKey(keyword)))) {
      return category;
    }
  }

  return null;
}

function cleanCodedValue(questionKey: RbimCodeQuestionKey, value: unknown) {
  const result = cleanRBIMCode(questionKey, typeof value === "number" ? value : String(value ?? ""));

  return result.error ? "" : result.label ?? "";
}

function isValidAge(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value) && value >= 0;
}

function isNotApplicableOrUnknown(value: string) {
  return ["", "99", "99 N A", "N A", "NA", "NOT APPLICABLE", "UNKNOWN"].includes(value);
}

function isAffirmativeDeathFlag(value: unknown) {
  const yesNo = normalizeYesNo(value);

  return yesNo === "Yes";
}

function normalizeComparable(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeStandardKey(value: unknown) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[’']/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toStableKey(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+([a-z0-9])/g, (_, character: string) => character.toUpperCase()) || "unknown";
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getSingleColumnDepth(column: ReportColumn): number {
  return column.children?.length ? 1 + Math.max(...column.children.map(getSingleColumnDepth)) : 1;
}

function fillHeaderRows(columns: ReportColumn[], rows: string[][], level: number, startColumn: number): number {
  let columnIndex = startColumn;

  columns.forEach((column) => {
    rows[level][columnIndex] = column.label;

    if (column.children?.length) {
      columnIndex = fillHeaderRows(column.children, rows, level + 1, columnIndex);
      return;
    }

    columnIndex += 1;
  });

  return columnIndex;
}
